import React, { useEffect, useRef, useState } from 'react';
import { FaInstagram, FaLinkedin } from 'react-icons/fa';
import axios from 'axios';
import { LuCheck } from 'react-icons/lu';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';

import WhatIsTribe from '../components/ReferralPartnerTraining/WhatIsTribe';
import MemberJourney from '../components/ReferralPartnerTraining/MemberJourney';
import YourRole from '../components/ReferralPartnerTraining/YourRole';
import WhoToInvite from '../components/ReferralPartnerTraining/WhoToInvite';
import WhereToFindPeople from '../components/ReferralPartnerTraining/WhereToFindPeople';
import OpeningMessage from '../components/ReferralPartnerTraining/OutreachScript';
import QualifiedReferral from '../components/ReferralPartnerTraining/ReferralDashboard';
import MonthlyExpectations from '../components/ReferralPartnerTraining/MonthlyExpectations';
import Compensation from '../components/ReferralPartnerTraining/Compensation';
import Rules from '../components/ReferralPartnerTraining/Rules';

const API = process.env.REACT_APP_API_URL || '';
const EMAIL_KEY = 'tribeReferralPartnerTrainingEmail';
const STEP_KEY = 'tribeReferralPartnerTrainingStep';
const QUIZ_SCORE_KEY = 'tribeReferralPartnerTrainingQuizScore';


const SECTIONS = [
  WhatIsTribe,
  MemberJourney,
  YourRole,
  WhoToInvite,
  WhereToFindPeople,
  OpeningMessage,
  QualifiedReferral,
  MonthlyExpectations,
  Compensation,
  Rules,
];

const FINAL_QUIZ = [

  [
    'q1',
    'What does Tribe do?',
    [
      'Organizes professional networking events',
      'Matches people with friends using an algorithm',
      'Connects people primarily through online group chats',
      'Helps strangers build real relationships through shared, in-person experiences',
    ],
  ],

  [
    'q2',
    'Which order best matches the ambassador recruiting flow?',
    [
      'Send the signup link → find a prospect → explain Tribe → check the dashboard',
      'Find a strong prospect → send the opening message → follow the script based on their response → send your referral code when they’re interested → track their progress in the dashboard',
      'Find a prospect → send your referral code → wait for them to attend → introduce Tribe afterward',
      'Post Tribe publicly → wait for signups → contact people who create accounts',
    ],
  ],

  [
    'q3',
    'What is your primary role as a Tribe ambassador?',
    [
      'Find strong prospects, reach out to them, and turn them into qualified referrals',
      'Host Tribe gatherings',
      'Create social media content for Tribe',
      'Handle customer support',
    ],
  ],

  [
    'q4',
    'You find a 25-year-old woman on Instagram who recently moved to Los Angeles from Boston. Her profile has photos with friends back home at dinners, birthdays, concerts, and other group activities. Her profile looks put-together, and there’s no indication that she’s married or has children. Should you invite her?',
    [
      'Only if she has a large social media following',
      'No. She already has friends, so she would not need Tribe',
      'Yes. She shows several signs of being a strong Tribe prospect',
      'No. Only people who have lived in Los Angeles for a long time should be invited',
    ],
  ],

  [
    'q5',
    'What should be your primary recruiting approach?',
    [
      'Post the Tribe signup link publicly',
      'Find strong prospects online and reach out to them individually',
      'Only invite people you already know',
      'Wait for friends to ask about Tribe',
    ],
  ],

  [
    'q6',
    'Which channels should you use to find prospects?',
    [
      'Only your personal contacts',
      'LinkedIn and job boards',
      'Tribe’s existing member list',
      'Instagram, Bumble BFF, TikTok, and other relevant social or friend-making platforms',
    ],
  ],

  [
    'q7',
    'You find a relevant Los Angeles community page on Instagram. What should you do next?',
    [
      'Post your Tribe link in the comments and wait for people to join',
      'Follow everyone on the page first, then wait to see who follows you back',
      'Quickly work down the page’s follower list, scan profiles for basic fit, and individually send the approved opening message to strong prospects',
      'Message the community page and ask them to recruit people for you',
    ],
  ],

  [
    'q8',
    'What is the outreach script for?',
    [
      'A branching conversation guide with approved messages based on how a prospect responds',
      'A list of people Tribe wants you to recruit',
      'A public advertisement you post on social media',
      'A phone script you have to read word-for-word',
    ],
  ],

  [
    'q9',
    'What is the Referral Dashboard used for?',
    [
      'Accessing your outreach script and referral code, and tracking signups, referral progress, earnings, and bonus progress',
      'Automatically finding prospects for you',
      'Messaging every Tribe member',
      'Creating and managing Tribe gatherings',
    ],
  ],

  [
    'q10',
    'When has someone actually become a qualified referral?',
    [
      'When they reply that they are interested',
      'When they create an account using your referral code',
      'When they create an account using your referral code and attend their first Tribe gathering',
      'When you send them an invitation',
    ],
  ],

  [
    'q11',
    'An ambassador creates fake or duplicate accounts to increase their referral numbers. What happens?',
    [
      'They still count if a valid referral code was used',
      'They count toward signup totals but not earnings',
      'They only lose that month’s bonus',
      'They do not count, and manipulating referrals results in removal from the program',
    ],
  ],

  [
    'q12',
    'You want to invite several people who already know one another. What should you do?',
    [
      'Put all of them into the same gathering so they feel more comfortable',
      'Spread them across different gatherings so an existing friend group does not make up a large portion of one gathering',
      'People who already know one another can never be invited to Tribe',
      'Have one person reserve spots for the entire group',
    ],
  ],

].map(([id, prompt, choices]) => ({

  id,

  prompt,

  choices: choices.map((label, index) => ({
    value: String.fromCharCode(65 + index),
    label,
  })),

}));


function Question({
  prompt,
  choices,
  correct,
  explanation,
  selected,
  onSelect,
}) {
  const hasAnswer = Boolean(selected);
  const isCorrect = selected === correct;

  return (
    <div className="micro-question">
      <div className="micro-question-label">QUICK CHECK</div>
      <h2>{prompt}</h2>
      <div className="micro-options">
        {choices.map((choice) => {
          const chosen = selected === choice.value;
          const correctChoice = hasAnswer && choice.value === correct;
          const wrongChoice = chosen && !isCorrect;

          return (
            <button
              type="button"
              key={choice.value}
              className={[
                'micro-option',
                chosen ? 'is-selected' : '',
                correctChoice ? 'is-correct' : '',
                wrongChoice ? 'is-wrong' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => onSelect(choice.value, correct)}
            >
              <span>{choice.value}</span>
              <strong>{choice.label}</strong>
            </button>
          );
        })}
      </div>

      {hasAnswer && (
        <div className={'answer-feedback ' + (isCorrect ? 'is-correct' : 'is-wrong')}>
          <strong>{isCorrect ? 'Correct' : 'Not quite'}</strong>
          <span>{isCorrect ? explanation : 'Try again. Look for the most direct next step.'}</span>
        </div>
      )}
    </div>
  );
}

export default function ReferralPartnerTraining() {
  const [screen, setScreen] = useState('access');
  const [email, setEmail] = useState('');
  const [training, setTraining] = useState(null);
  const [sectionIndex, setSectionIndex] = useState(0);
  const [partIndex, setPartIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [answerCorrect, setAnswerCorrect] = useState(false);
  const [activityComplete, setActivityComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [audioStatus, setAudioStatus] = useState('');
  const [playbackId, setPlaybackId] = useState(0);

  const [quizIndex, setQuizIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [confirmQuizStart, setConfirmQuizStart] = useState(false);
  const [confirmReviewTraining, setConfirmReviewTraining] = useState(false);

  const audioRef = useRef(null);
  const requestRef = useRef(0);
  const abortRef = useRef(null);
  const objectUrlRef = useRef('');

  const ActiveSection = SECTIONS[sectionIndex];
  const activePart = ActiveSection?.steps?.[partIndex];
  const isQuestion = activePart?.type === 'question';
  const isActivity = activePart?.type === 'activity';
  const isInteractive = isQuestion || isActivity;
  const totalSections = SECTIONS.length;
  const totalParts = ActiveSection?.steps?.length || 1;

  useEffect(() => {
    const savedEmail = localStorage.getItem(EMAIL_KEY);
    if (savedEmail) setEmail(savedEmail);
  }, []);

  useEffect(() => {
    setSelectedAnswer('');
    setAnswerCorrect(false);
    setActivityComplete(false);
    setError('');
    localStorage.setItem(STEP_KEY, JSON.stringify({ sectionIndex, partIndex }));
  }, [sectionIndex, partIndex]);

  useEffect(() => {
    if (screen !== 'training' || !email || !activePart || isInteractive) {
      stopAudio();
      return undefined;
    }

    const requestId = ++requestRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setAudioStatus('Loading audio…');

    axios.post(
      API + '/referral-partners/training/narration',
      { email, slide: sectionIndex + 1, part: partIndex },
      { responseType: 'blob', signal: controller.signal }
    ).then(async (response) => {
      if (controller.signal.aborted || requestId !== requestRef.current) return;

      const nextUrl = URL.createObjectURL(response.data);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = nextUrl;
      setAudioUrl(nextUrl);

      const audio = audioRef.current;
      if (!audio) return;
      audio.src = nextUrl;
      audio.currentTime = 0;
      audio.load();

      try {
        await audio.play();
        setAudioStatus('Playing');
      } catch {
        setAudioStatus('Press play to hear this part');
      }
    }).catch((err) => {
      if (!controller.signal.aborted && err?.code !== 'ERR_CANCELED') {
        console.error('[ReferralPartnerTraining] narration', err);
        setAudioStatus('Audio unavailable');
      }
    });

    return () => controller.abort();
  }, [screen, email, sectionIndex, partIndex, activePart, isInteractive]);

  useEffect(() => () => {
    requestRef.current += 1;
    if (abortRef.current) abortRef.current.abort();
    if (audioRef.current) audioRef.current.pause();
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  function stopAudio() {
    requestRef.current += 1;
    if (abortRef.current) abortRef.current.abort();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
    }
    setAudioUrl('');
    setAudioStatus('');
  }

  function errorMessage(err, fallback) {
    return err?.response?.data?.message || err?.message || fallback;
  }

  async function reviewContent() {
    setLoading(true);
    setError('');
    stopAudio();

    try {
      const { data = {} } = await axios.post(
        API + '/referral-partners/training/start',
        { email }
      );

      setTraining(data.training || training);
      setSectionIndex(0);
      setPartIndex(0);
      setSelectedAnswer('');
      setAnswerCorrect(false);
      setActivityComplete(false);
      setScreen('training');
      scrollTop();
    } catch (err) {
      setError(errorMessage(err, 'Unable to review training.'));
    } finally {
      setLoading(false);
    }
  }

  function scrollTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function restorePart(savedSlide) {
    try {
      const saved = JSON.parse(localStorage.getItem(STEP_KEY) || '{}');
      if (saved.sectionIndex === savedSlide - 1 && Number.isInteger(saved.partIndex)) {
        return Math.max(0, Math.min(SECTIONS[savedSlide - 1].steps.length - 1, saved.partIndex));
      }
    } catch {
      // Ignore invalid local progress.
    }
    return 0;
  }

  async function checkAccess(event) {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      const message = 'Enter the email you used on your application.';
      setError(message);
      window.setTimeout(() => {
        setError((current) => current === message ? '' : current);
      }, 3000);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const { data = {} } = await axios.post(API + '/referral-partners/training/access', { email: normalizedEmail });
      const savedSlide = Math.max(1, Math.min(totalSections, Number(data.training?.currentSlide || 1)));
      localStorage.setItem(EMAIL_KEY, normalizedEmail);
      setEmail(normalizedEmail);
      setTraining(data.training || null);
      setSectionIndex(savedSlide - 1);
      setPartIndex(restorePart(savedSlide));
      setScreen('intro');
      scrollTop();
    } catch (err) {
      const message = errorMessage(err, 'We could not verify your training access.');
      setError(message);
      window.setTimeout(() => {
        setError((current) => current === message ? '' : current);
      }, 3000);
    } finally {
      setLoading(false);
    }
  }

  async function startTraining() {
    setLoading(true);
    setError('');

    try {
      const { data = {} } = await axios.post(
        API + '/referral-partners/training/start',
        { email }
      );

      setTraining(data.training || null);

      const savedSlide = Math.max(
        1,
        Math.min(
          totalSections,
          Number(
            data.training?.currentSlide ||
            1
          )
        )
      );

      setSectionIndex(
        savedSlide - 1
      );

      setPartIndex(
        restorePart(
          savedSlide
        )
      );

      setScreen('training');
      scrollTop();
    } catch (err) {
      setError(
        errorMessage(
          err,
          'Unable to start training.'
        )
      );
    } finally {
      setLoading(false);
    }
  }


  async function beginQuizAttempt() {
    setLoading(true);
    setError('');

    try {
      const { data = {} } = await axios.post(
        API + '/referral-partners/training/quiz/start',
        { email }
      );

      if (data.alreadyPassed || data.passed) {
        setTraining(data.training || training);
        setQuizResult({
          passed: true,
          percentage:
            data.training?.bestQuizScore ??
            training?.bestQuizScore ??
            0,
          passingPercentage: 80,
          attempts:
            data.training?.quizAttempts ??
            training?.quizAttempts ??
            0,
        });
        setScreen('result');
        scrollTop();
        return;
      }

      setTraining(data.training || training);
      setQuizAnswers({});
      setQuizIndex(0);
      setQuizResult(null);
      setScreen('quiz');
      scrollTop();
    } catch (err) {
      setError(
        errorMessage(
          err,
          'Unable to start the final quiz.'
        )
      );
    } finally {
      setLoading(false);
    }
  }


  async function beginFinalQuizFromTraining() {
    setLoading(true);
    setError('');
    stopAudio();

    try {
      const currentSlide =
        sectionIndex + 1;

      let nextTraining =
        training;

      if (!training?.moduleCompletedAt) {
        const progress =
          await axios.patch(
            API + '/referral-partners/training/progress',
            {
              email,
              currentSlide,
              completedSlide:
                currentSlide,
            }
          );

        const complete =
          await axios.post(
            API + '/referral-partners/training/complete',
            { email }
          );

        nextTraining =
          complete.data?.training ||
          progress.data?.training ||
          training;

        setTraining(
          nextTraining
        );

        localStorage.removeItem(
          STEP_KEY
        );
      }

      const { data = {} } =
        await axios.post(
          API + '/referral-partners/training/quiz/start',
          { email }
        );

      setTraining(
        data.training ||
        nextTraining
      );

      setQuizAnswers({});
      setQuizIndex(0);
      setQuizResult(null);
      setConfirmQuizStart(false);
      setScreen('quiz');
      scrollTop();
    } catch (err) {
      setError(
        errorMessage(
          err,
          'Unable to start the final quiz.'
        )
      );
    } finally {
      setLoading(false);
    }
  }


  function handleAnswer(value, correct) {
    setSelectedAnswer(value);
    setAnswerCorrect(value === correct);
  }

  async function goForward() {
    if (
      loading ||
      (isQuestion && !answerCorrect) ||
      (activePart?.requiresCompletion && !activityComplete)
    ) return;

    if (partIndex < totalParts - 1) {
      setPartIndex((current) => current + 1);
      scrollTop();
      return;
    }

    setLoading(true);
    setError('');
    stopAudio();

    try {
      const currentSlide = sectionIndex + 1;

      if (sectionIndex === totalSections - 1) {
        const progress = await axios.patch(API + '/referral-partners/training/progress', {
          email,
          currentSlide,
          completedSlide: currentSlide,
        });
        const complete = await axios.post(API + '/referral-partners/training/complete', { email });
        setTraining(complete.data?.training || progress.data?.training || training);
        localStorage.removeItem(STEP_KEY);
        setQuizIndex(0);
        setScreen('quiz');
      } else {
        const nextSlide = currentSlide + 1;
        const { data = {} } = await axios.patch(API + '/referral-partners/training/progress', {
          email,
          currentSlide: nextSlide,
          completedSlide: currentSlide,
        });
        setTraining(data.training || training);
        setSectionIndex((current) => current + 1);
        setPartIndex(0);
      }
      scrollTop();
    } catch (err) {
      setError(errorMessage(err, 'Unable to save your progress.'));
    } finally {
      setLoading(false);
    }
  }

  async function goBack() {
    if (loading) return;
    if (partIndex > 0) {
      setPartIndex((current) => current - 1);
      scrollTop();
      return;
    }
    if (sectionIndex === 0) return;

    const previousSection = sectionIndex - 1;
    setLoading(true);
    setError('');
    stopAudio();
    try {
      await axios.patch(API + '/referral-partners/training/progress', {
        email,
        currentSlide: previousSection + 1,
      });
      setSectionIndex(previousSection);
      setPartIndex(SECTIONS[previousSection].steps.length - 1);
      scrollTop();
    } catch (err) {
      setError(errorMessage(err, 'Unable to move back.'));
    } finally {
      setLoading(false);
    }
  }

  async function replayAudio() {
  const audio = audioRef.current;

  if (!audio || !audioUrl) return;

  audio.pause();
  audio.currentTime = 0;
  setPlaybackId((current) => current + 1);

  try {
    await audio.play();
    setAudioStatus('Playing');
  } catch {
    setAudioStatus('Unable to play audio');
  }
}

  async function submitFinalQuiz() {
    if (FINAL_QUIZ.some((question) => !quizAnswers[question.id])) {
      setError('Answer every question before submitting.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const answers = FINAL_QUIZ.map((question) => ({
        questionId: question.id,
        answer: quizAnswers[question.id],
      }));
      const { data = {} } = await axios.post(API + '/referral-partners/training/quiz', { email, answers });

      if (
        data.percentage !== null &&
        data.percentage !== undefined &&
        data.percentage !== '' &&
        Number.isFinite(Number(data.percentage))
      ) {
        localStorage.setItem(
          `${QUIZ_SCORE_KEY}:${email}`,
          String(Number(data.percentage))
        );
      }

      setTraining(
        data.training ||
        training
      );
      setQuizResult(data);
      setScreen('result');
      scrollTop();
    } catch (err) {
      setError(errorMessage(err, 'Unable to submit your quiz.'));
    } finally {
      setLoading(false);
    }
  }

  if (screen === 'access') {
    return (
      <Page>
        <main className="access-ambient">
          <header className="access-ambient-header">
            <Logo />
            <div className="access-module-name">BRAND AMBASSADOR TRAINING MODULE</div>
          </header>

          <div className="access-ambient-stage" aria-hidden="true">
            <div className="ambient-card ambient-dashboard">
              <div className="ambient-dashboard-top">
                <span className="ambient-dot" />
                <span className="ambient-line ambient-line-long" />
              </div>
              <div className="ambient-dashboard-row">
                <span className="ambient-avatar" />
                <span className="ambient-line" />
                <span className="ambient-pill" />
              </div>
              <div className="ambient-dashboard-row">
                <span className="ambient-avatar" />
                <span className="ambient-line ambient-line-short" />
                <span className="ambient-pill ambient-pill-dark" />
              </div>
            </div>

            <div className="ambient-float ambient-profile">
              <span className="ambient-profile-avatar">M</span>
              <div>
                <strong>Strong prospect</strong>
                <small>Los Angeles · 20s</small>
              </div>
            </div>

            <div className="ambient-float ambient-message">
              <span className="ambient-message-dot" />
              <p>You seem cool! I wanted to offer you an invite.</p>
            </div>

            <div className="ambient-float ambient-qualified">
              <span>✓</span>
              <div>
                <strong>Qualified referral</strong>
                <small>First gathering attended</small>
              </div>
            </div>

            <div className="ambient-float ambient-step">
              <span>01</span>
              <strong>Find the right people</strong>
            </div>
          </div>

          <section className="access-ambient-content">
            <h1>Get started with your email</h1>

            <form onSubmit={checkAccess}>
              <div className="access-prompt-input">
                <input
                  id="training-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="type here"
                  aria-label="Email address"
                />
              </div>

              <Button type="submit" wide disabled={loading}>
                {loading ? 'Checking…' : 'Get Started'}
              </Button>

              <p className="access-form-note">
                This module shows you how to identify strong prospects, reach out using Tribe’s process,
                and turn interest into qualified referrals. Use the email from your application to begin.
              </p>

              {error && <Error>{error}</Error>}
            </form>
          </section>

          <footer className="access-ambient-footer">
            <div className="access-legal">
              © 2026 Tribe, Inc. All rights reserved.
            </div>

            <div className="access-centered-socials">
              <a
                href="https://www.instagram.com/jointribe.io/"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
              >
                <FaInstagram />
              </a>

              <a
                href="https://www.linkedin.com/company/82302259"
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
              >
                <FaLinkedin />
              </a>
            </div>
          </footer>
        </main>
      </Page>
    );
  }

  if (screen === 'intro') {
    const parseActualQuizScore = (value) => {
      if (value === null || value === undefined || value === '') return null;
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };

    const introQuizScore =
      parseActualQuizScore(
        quizResult?.percentage
      ) ??
      parseActualQuizScore(
        training?.bestQuizScore
      ) ??
      parseActualQuizScore(
        localStorage.getItem(
          `${QUIZ_SCORE_KEY}:${email}`
        )
      );

    const hasPassedQuiz =
      quizResult?.passed === true ||
      Boolean(
        training?.quizPassedAt
      );

    const quizInProgress =
      training?.quizInProgress ===
      true;

    const quizLocked =
      training?.quizLocked ===
      true;

    const quizAttempts =
      Number(
        training?.quizAttempts ||
        0
      );

    const maxQuizAttempts =
      Number(
        training?.maxQuizAttempts ||
        4
      );

    const moduleComplete =
      Boolean(
        training?.moduleCompletedAt
      );

    const canReview =
      training?.canReviewTraining !==
      false &&
      !quizInProgress &&
      !quizLocked;

    const startLabel =
      hasPassedQuiz
        ? 'Passed'
        : quizLocked
          ? 'Not passed'
          : quizInProgress
            ? 'Resume quiz'
            : moduleComplete
              ? quizAttempts > 0
                ? 'Retake quiz'
                : 'Start final quiz'
              : training?.startedAt
                ? 'Continue training'
                : 'Start training';

    const handleIntroPrimary =
      async () => {
        if (
          hasPassedQuiz ||
          quizLocked
        ) {
          return;
        }

        if (quizInProgress) {
          await beginQuizAttempt();
          return;
        }

        if (moduleComplete) {
          setConfirmQuizStart(true);
          return;
        }

        await startTraining();
      };

    const introCurrentSection = Math.max(
      1,
      Math.min(totalSections, Number(training?.currentSlide || sectionIndex + 1 || 1))
    );

    const introVisibleStart = Math.max(
      0,
      Math.min(totalSections - 6, introCurrentSection - 3)
    );

    const introVisibleSections = SECTIONS.slice(
      introVisibleStart,
      introVisibleStart + 6
    );

    const introIsEnding = introCurrentSection >= totalSections - 2;
    const introEndShift = Math.min(
      54,
      Math.max(0, introCurrentSection - (totalSections - 3)) * 18
    );

    return (
      <Page>
        <main className="intro-open">
          <header className="intro-open-header">
            <Logo />
            <div className="intro-open-module-name">BRAND AMBASSADOR TRAINING MODULE</div>
          </header>

          <div className="intro-background-details" aria-hidden="true">
            <div className="intro-bg-card intro-bg-card-one intro-bg-prospect">
              <span className="intro-bg-avatar">M</span>
              <div>
                <strong>Strong prospect</strong>
                <small>Maya, 26 · Los Angeles</small>
              </div>
              <span className="intro-bg-pill">FIT</span>
            </div>

            <div className="intro-bg-card intro-bg-card-two intro-bg-outreach">
              <span className="intro-bg-dot" />
              <p>You seem cool! I wanted to offer you an invite.</p>
            </div>

            <div className="intro-bg-card intro-bg-card-three intro-bg-qualified">
              <span className="intro-bg-check">✓</span>
              <div>
                <strong>Qualified referral</strong>
                <small>First gathering attended</small>
              </div>
            </div>
          </div>

          <section className="intro-open-main">
            <div className="intro-open-copy">
              <h1>Brand Ambassador Training Overview</h1>

              <p>
This training walks you through the process you’ll use as a Tribe ambassador. Each section includes a short lesson followed by a knowledge check. After completing all 10 sections, you’ll have up to 4 attempts to pass the final quiz.
  </p>

              <div className="intro-open-stats">
                <div>
                  <strong>10</strong>
                  <span>sections</span>
                </div>

                <div>
                  <strong>
                    {introQuizScore === null ? 'N/A' : `${Math.round(introQuizScore)}%`}
                  </strong>
                  <span>quiz score</span>
                </div>

                <div className="intro-account-stat" title={email}>
                  <strong>{email || '—'}</strong>
                  <span>applicant</span>
                </div>
              </div>

              <div className="intro-open-actions">
                <button
                  type="button"
                  className={[
                    'training-button',
                    (hasPassedQuiz || quizLocked)
                      ? 'intro-status-button'
                      : '',
                  ].filter(Boolean).join(' ')}
                  onClick={handleIntroPrimary}
                  disabled={
                    loading ||
                    hasPassedQuiz ||
                    quizLocked
                  }
                >
                  {hasPassedQuiz && <LuCheck size={15} />}
                  {loading ? 'Loading…' : startLabel}
                </button>

                {moduleComplete && canReview && (
                  <button
                    type="button"
                    className="intro-review-content"
                    onClick={reviewContent}
                    disabled={loading}
                  >
                    Review content
                  </button>
                )}
              </div>

              {error && <Error>{error}</Error>}
            </div>

            <div
              className={'intro-stepper ' + (introIsEnding ? 'is-ending' : '')}
              aria-label="Training progress"
            >
              <div
                className="intro-stepper-list"
                style={{
                  transform: introEndShift
                    ? `translateY(-${introEndShift}px)`
                    : 'translateY(0)',
                }}
              >
                {introVisibleSections.map((Section, visibleIndex) => {
                  const sectionNumber = introVisibleStart + visibleIndex + 1;
                  const isComplete =
                    training?.moduleCompletedAt || sectionNumber < introCurrentSection;
                  const isCurrent =
                    !training?.moduleCompletedAt && sectionNumber === introCurrentSection;

                  return (
                    <div
                      key={sectionNumber}
                      className={[
                        'intro-stepper-item',
                        isComplete ? 'is-complete' : '',
                        isCurrent ? 'is-current' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      {isCurrent && (
                        <div className="intro-you-are-here">YOU'RE HERE</div>
                      )}

                      <span className="intro-stepper-node">
                        {isComplete ? '✓' : ''}
                      </span>

                      <div className="intro-stepper-copy">
                        <strong>{Section.title || `Section ${sectionNumber}`}</strong>
                        {isCurrent && <small>Current section</small>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {confirmQuizStart && (
            <div
              className="quiz-confirm-backdrop"
              role="presentation"
              onClick={() => setConfirmQuizStart(false)}
            >
              <div
                className="quiz-confirm-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="intro-quiz-confirm-title"
                onClick={(event) => event.stopPropagation()}
              >
                <strong id="intro-quiz-confirm-title">
                  Ready for the final quiz?
                </strong>
                <p>
                  You’ll answer 12 questions. This will be attempt{' '}
                  {Math.min(quizAttempts + 1, maxQuizAttempts)} of {maxQuizAttempts}.
                  Once you start, training content is locked until you submit the attempt.
                </p>

                <div className="quiz-confirm-actions">
                  <Button
                    secondary
                    onClick={() => {
                      setConfirmQuizStart(false);
                      reviewContent();
                    }}
                    disabled={loading}
                  >
                    Keep reviewing
                  </Button>

                  <Button
                    onClick={() => {
                      setConfirmQuizStart(false);
                      beginQuizAttempt();
                    }}
                    disabled={loading}
                  >
                    Start quiz
                  </Button>
                </div>
              </div>
            </div>
          )}

          <footer className="intro-open-footer">
            <span>© 2026 Tribe, Inc. All rights reserved.</span>
          </footer>
        </main>
      </Page>
    );
  }

  if (screen === 'training') {
    const railProgress = totalSections > 1
      ? (sectionIndex / (totalSections - 1)) * 90
      : 0;

    return (
      <Page>
        <audio
          ref={audioRef}
          preload="auto"
          onPlay={() => setAudioStatus('Playing')}
          onPause={() => {
            if (audioRef.current && audioRef.current.currentTime > 0 && !audioRef.current.ended) setAudioStatus('Paused');
          }}
          onEnded={() => setAudioStatus('Complete')}
        />
        <main className="training-player">
          <header className="training-header">
            <Logo />

            <div className="training-header-progress" aria-label={`Section ${sectionIndex + 1} of ${totalSections}`}>
              <div className="training-step-progress">
                <div className="training-step-line" />
                <div
                  className="training-step-active-line"
                  style={{ width: `${railProgress}%` }}
                />
                {SECTIONS.map((_, stepIndex) => {
                  const isComplete = stepIndex < sectionIndex;
                  const isCurrent = stepIndex === sectionIndex;

                  return (
                    <span
                      key={stepIndex}
                      className={[
                        'training-step-node',
                        isComplete ? 'is-complete' : '',
                        isCurrent ? 'is-current' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      {isComplete ? <LuCheck size={8} /> : null}
                    </span>
                  );
                })}
              </div>

              <div className="training-progress-context">
                <strong>{ActiveSection.title}</strong>
                <i>·</i>
                <span>Section {sectionIndex + 1} of {totalSections}</span>
              </div>
            </div>

            <div className="training-header-title">BRAND AMBASSADOR TRAINING MODULE</div>
          </header>


          <div className="section-stage" key={sectionIndex + '-' + partIndex}>
            <ActiveSection
  part={partIndex}
  Question={Question}
  selected={selectedAnswer}
  onSelect={handleAnswer}
  onComplete={() => {
    setActivityComplete(true);
    setAnswerCorrect(true);
  }}
  audioStatus={audioStatus}
  playbackId={playbackId}
/>
          </div>

          {error && <Error>{error}</Error>}

          <footer className="training-controls">
            <div>
              <Button secondary onClick={goBack} disabled={loading || (sectionIndex === 0 && partIndex === 0)}>Back</Button>
              {!isInteractive && (
                <button type="button" className="audio-button" onClick={replayAudio} disabled={!audioUrl || loading}>
                  ↻ Play again
                </button>
              )}
              {!isInteractive && <small>{audioStatus}</small>}
            </div>

            <div className="training-next-group">
              <Button
                onClick={() => {
                  const isFinalTrainingStep =
                    partIndex === totalParts - 1 &&
                    sectionIndex === totalSections - 1;

                  if (isFinalTrainingStep) {
                    setConfirmQuizStart(true);
                    return;
                  }

                  goForward();
                }}
                disabled={
                  loading ||
                  (isQuestion && !answerCorrect) ||
                  (activePart?.requiresCompletion && !activityComplete)
                }
              >
                {loading ? 'Saving…' : partIndex === totalParts - 1 && sectionIndex === totalSections - 1 ? 'Start final quiz' : 'Continue'}
              </Button>
            </div>
          </footer>

          {confirmQuizStart && (
            <div
              className="quiz-confirm-backdrop"
              role="presentation"
              onClick={() => setConfirmQuizStart(false)}
            >
              <div
                className="quiz-confirm-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="quiz-confirm-title"
                onClick={(event) => event.stopPropagation()}
              >
                <strong id="quiz-confirm-title">Ready for the final quiz?</strong>
                <p>
                  You’ll answer 12 questions. This will be attempt{' '}
                  {Math.min(
                    Number(training?.quizAttempts || 0) + 1,
                    Number(training?.maxQuizAttempts || 4)
                  )}{' '}
                  of {Number(training?.maxQuizAttempts || 4)}.
                  Once you start, training content is locked until you submit the attempt.
                </p>

                <div className="quiz-confirm-actions">
                  <Button
                    secondary
                    onClick={() => setConfirmQuizStart(false)}
                  >
                    Keep reviewing
                  </Button>

                  <Button
                    onClick={beginFinalQuizFromTraining}
                    disabled={loading}
                  >
                    {loading ? 'Starting…' : 'Start quiz'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="training-document-footer" aria-hidden="true">
            © 2026 Tribe, Inc. All rights reserved.
          </div>
        </main>
      </Page>
    );
  }

  if (screen === 'quiz') {
    const question = FINAL_QUIZ[quizIndex];
    const selected = quizAnswers[question.id];
    const QUIZ_VISIBLE_STEPS = 8;

    /*
     * Keep all 12 real quiz steps.
     * Questions 1–8 stay in the initial viewport.
     * Beginning with question 9, slide the full 12-step rail
     * left one step at a time so later questions come into view.
     */
    const quizRailWindowStart =
      Math.max(
        0,
        Math.min(
          FINAL_QUIZ.length - QUIZ_VISIBLE_STEPS,
          quizIndex - (QUIZ_VISIBLE_STEPS - 1)
        )
      );

    const quizRailProgress =
      FINAL_QUIZ.length > 1
        ? (quizIndex / (FINAL_QUIZ.length - 1)) * 90
        : 0;

    /*
     * translateX percentages are relative to the full rail width.
     * One real quiz step = 1 / 12 of that width.
     */
    const quizRailShift =
      quizRailWindowStart *
      (100 / FINAL_QUIZ.length);

    const quizRailHasHiddenBefore =
      quizRailWindowStart > 0;

    const quizRailHasHiddenAfter =
      quizRailWindowStart + QUIZ_VISIBLE_STEPS <
      FINAL_QUIZ.length;

    return (
      <Page>
        <main className="final-quiz">
          <header className="training-header">
            <Logo />

            <div
              className="training-header-progress final-quiz-header-progress"
              aria-label={`Question ${quizIndex + 1} of ${FINAL_QUIZ.length}`}
            >
              <div
                className={[
                  'quiz-step-viewport',
                  quizRailHasHiddenBefore ? 'has-hidden-before' : '',
                  quizRailHasHiddenAfter ? 'has-hidden-after' : '',
                ].filter(Boolean).join(' ')}
              >
                <div
                  className="training-step-progress quiz-step-progress"
                  style={{
                    transform: `translateX(-${quizRailShift}%)`,
                  }}
                >
                  <div className="training-step-line" />

                  <div
                    className="training-step-active-line"
                    style={{ width: `${quizRailProgress}%` }}
                  />

                  {FINAL_QUIZ.map((_, stepIndex) => {
                    const isComplete = stepIndex < quizIndex;
                    const isCurrent = stepIndex === quizIndex;

                    return (
                      <span
                        key={stepIndex}
                        className={[
                          'training-step-node',
                          isComplete ? 'is-complete' : '',
                          isCurrent ? 'is-current' : '',
                        ].filter(Boolean).join(' ')}
                      >
                        {isComplete ? <LuCheck size={8} /> : null}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className="training-progress-context">
                <strong>Final Quiz</strong>
              </div>
            </div>

            <div className="training-header-title">BRAND AMBASSADOR TRAINING MODULE</div>
          </header>


          <section>
            <div className="eyebrow">
              QUESTION {quizIndex + 1}/{FINAL_QUIZ.length}
            </div>
<h1
  className={[
    question.prompt.length > 260 ? 'is-very-long' : '',
    question.prompt.length > 160 && question.prompt.length <= 260
      ? 'is-long'
      : '',
  ].filter(Boolean).join(' ')}
>
  {question.prompt}
</h1>
            <div className="final-options">
              {question.choices.map((choice) => (
                <button
                  type="button"
                  className={selected === choice.value ? 'is-selected' : ''}
                  onClick={() => {
                    setQuizAnswers((current) => ({ ...current, [question.id]: choice.value }));
                    setError('');
                  }}
                  key={choice.value}
                >
                  <span>{choice.value}</span><strong>{choice.label}</strong>
                </button>
              ))}
            </div>
            {error && <Error>{error}</Error>}
            <nav>
              <Button secondary onClick={() => setQuizIndex((current) => current - 1)} disabled={quizIndex === 0 || loading}>Back</Button>

              <div className="training-next-group">
                {quizIndex === FINAL_QUIZ.length - 1
                  ? <Button onClick={submitFinalQuiz} disabled={!selected || loading}>{loading ? 'Submitting…' : 'Submit quiz'}</Button>
                  : <Button onClick={() => setQuizIndex((current) => current + 1)} disabled={!selected}>Continue</Button>}
              </div>
            </nav>
          </section>

          <div className="training-document-footer" aria-hidden="true">
            © 2026 Tribe, Inc. All rights reserved.
          </div>
        </main>
      </Page>
    );
  }

  if (screen === 'result') {
    /*
     * overallPassed is permanent once earned.
     * A lower improvement attempt never turns a passed candidate
     * back into a failed candidate.
     */
    const passed =
      quizResult?.overallPassed === true ||
      quizResult?.passed === true ||
      Boolean(training?.quizPassedAt);

    const attemptsUsed =
      Number(
        quizResult?.attempts ??
        training?.quizAttempts ??
        0
      );

    const maxAttempts =
      Number(
        quizResult?.maxAttempts ??
        training?.maxQuizAttempts ??
        4
      );

    const attemptsRemaining =
      Number(
        quizResult?.attemptsRemaining ??
        Math.max(
          0,
          maxAttempts -
            attemptsUsed
        )
      );

    const totalQuestions =
      Number(
        quizResult?.totalQuestions ??
        FINAL_QUIZ.length
      );

    const attemptScore =
      Number(
        quizResult?.score ??
        0
      );

    const passingPercentage =
      Number(
        quizResult?.passingPercentage ??
        80
      );

    const passingScore =
      Math.ceil(
        totalQuestions *
        (
          passingPercentage /
          100
        )
      );

    /*
     * Passed users see their retained best score.
     * This keeps the X/X result consistent with the permanent
     * highest-score rule after an improvement attempt.
     */
    const bestPercentage =
      Number(
        quizResult?.bestQuizScore ??
        training?.bestQuizScore ??
        quizResult?.percentage ??
        0
      );

    const bestScoreFromPercentage =
      Math.max(
        0,
        Math.min(
          totalQuestions,
          Math.round(
            (
              bestPercentage /
              100
            ) *
            totalQuestions
          )
        )
      );

    const displayScore =
      passed
        ? Math.max(
            attemptScore,
            bestScoreFromPercentage
          )
        : attemptScore;

    const finalFailure =
      quizResult?.finalFailure ===
      true ||
      (
        !passed &&
        attemptsUsed >=
          maxAttempts
      );

    const returnToOverview = () => {
      setConfirmQuizStart(false);
      setConfirmReviewTraining(false);
      setScreen('intro');
      scrollTop();
    };

    return (
      <Page>
        <main className="result-page">
          <header className="training-header result-header">
            <Logo />

            <div className="result-header-center">
              Quiz Result
            </div>

            <div className="training-header-title">
              BRAND AMBASSADOR TRAINING MODULE
            </div>
          </header>

          <section className="result-stage">
            <div className="quiz-result-card">
              <div className="quiz-result-count">
                <strong>{displayScore}</strong>
                <span>/ {totalQuestions}</span>
              </div>

              <h1>
                {passed
                  ? 'You passed.'
                  : 'You didn’t pass.'}
              </h1>

              <p className="quiz-result-detail">
                {passed
                  ? attemptsRemaining > 0
                    ? `Your highest score is saved. ${attemptsRemaining} ${attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining if you want to improve it.`
                    : `Your highest score is saved. You’ve used all ${maxAttempts} attempts.`
                  : finalFailure
                    ? `You needed ${passingScore}/${totalQuestions} (${passingPercentage}%) to pass. You’ve used all ${maxAttempts} attempts.`
                    : `You need ${passingScore}/${totalQuestions} (${passingPercentage}%) to pass. ${attemptsRemaining} ${attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining.`}
              </p>

              <div className="quiz-result-actions">
                <Button
                  onClick={
                    passed || finalFailure
                      ? returnToOverview
                      : () => setConfirmQuizStart(true)
                  }
                  disabled={loading}
                >
                  {passed || finalFailure
                    ? 'Back to overview'
                    : 'Try again'}
                </Button>

                {passed && attemptsRemaining > 0 && (
                  <button
                    type="button"
                    className="quiz-result-text-action"
                    onClick={() => setConfirmQuizStart(true)}
                    disabled={loading}
                  >
                    Try again to improve score
                  </button>
                )}

                {!passed && !finalFailure && (
                  <button
                    type="button"
                    className="quiz-result-text-action"
                    onClick={() => setConfirmReviewTraining(true)}
                    disabled={loading}
                  >
                    Review training
                  </button>
                )}
              </div>
            </div>
          </section>

          {confirmQuizStart && !finalFailure && (
            <div
              className="quiz-confirm-backdrop"
              role="presentation"
              onClick={() => setConfirmQuizStart(false)}
            >
              <div
                className="quiz-confirm-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="result-quiz-confirm-title"
                onClick={(event) => event.stopPropagation()}
              >
                <strong id="result-quiz-confirm-title">
                  {passed
                    ? 'Try again to improve your score?'
                    : 'Ready to try again?'}
                </strong>

                <p>
                  {passed
                    ? `Your current best score of ${displayScore}/${totalQuestions} is already saved. This will be attempt ${Math.min(attemptsUsed + 1, maxAttempts)} of ${maxAttempts}. A lower score will not replace your best score. Once you start, training content is locked until you submit the attempt.`
                    : `You’ll answer ${totalQuestions} questions. This will be attempt ${Math.min(attemptsUsed + 1, maxAttempts)} of ${maxAttempts}. Once you start, training content is locked until you submit the attempt.`}
                </p>

                <div className="quiz-confirm-actions">
                  <Button
                    secondary
                    onClick={() => setConfirmQuizStart(false)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>

                  <Button
                    onClick={() => {
                      setConfirmQuizStart(false);
                      beginQuizAttempt();
                    }}
                    disabled={loading}
                  >
                    {loading ? 'Starting…' : 'Start quiz'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {confirmReviewTraining && !passed && !finalFailure && (
            <div
              className="quiz-confirm-backdrop"
              role="presentation"
              onClick={() => setConfirmReviewTraining(false)}
            >
              <div
                className="quiz-confirm-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="review-training-confirm-title"
                onClick={(event) => event.stopPropagation()}
              >
                <strong id="review-training-confirm-title">
                  Review training from the beginning?
                </strong>

                <p>
                  You’ll return to Section 1 and can go through the full training again. Your quiz result and remaining attempts will be saved.
                </p>

                <div className="quiz-confirm-actions">
                  <Button
                    secondary
                    onClick={() => setConfirmReviewTraining(false)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>

                  <Button
                    onClick={() => {
                      setConfirmReviewTraining(false);
                      reviewContent();
                    }}
                    disabled={loading}
                  >
                    {loading ? 'Loading…' : 'Review training'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="training-document-footer" aria-hidden="true">
            © 2026 Tribe, Inc. All rights reserved.
          </div>
        </main>
      </Page>
    );
  }


  return null;
}

function Page({ children, center = false }) {
  return <div className={'referral-training ' + (center ? 'is-centered' : '')}><style>{STYLES}</style>{children}</div>;
}

function Logo() {
  return <TribeLogo className="training-logo" role="img" aria-label="Tribe" />;
}

function Button({ children, secondary = false, wide = false, ...props }) {
  return <button type="button" className={'training-button ' + (secondary ? 'is-secondary ' : '') + (wide ? 'is-wide' : '')} {...props}>{children}</button>;
}

function Error({ children }) {
  return <div className="training-error">{children}</div>;
}

const STYLES = `
  :root {
    --training-ink: #111111;
    --training-muted: #707070;
    --training-line: #dddddd;
    --training-soft: #f5f5f5;
    --training-white: #ffffff;
    --training-canvas: #f6f6f4;
    --training-paper-edge: #e2e2de;
    --training-success: #16855b;
    --training-error: #b33a34;
  }

  * { box-sizing: border-box; }
  button, input { font: inherit; }

  .referral-training {
    min-height: 100vh;
    background: var(--training-canvas);
    color: var(--training-ink);
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .referral-training.is-centered {
    display: grid;
    place-items: center;
    padding: 28px;
    background: var(--training-white);
  }

  .training-logo {
    display: block;
    width: 72px;
    height: auto;
  }

  /* Ambient onboarding-style access screen */
  .access-ambient {
    position: relative;
    min-height: 100vh;
    overflow: hidden;
    display: grid;
    grid-template-rows: auto 1fr auto;
    padding: 30px 44px 24px;
    background:
      radial-gradient(circle at 50% 47%, rgba(246,246,244,.96) 0, rgba(251,251,250,.76) 27%, rgba(255,255,255,0) 55%),
      #ffffff;
  }

  .access-ambient-header {
    position: relative;
    z-index: 6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 56px;
  }

  .access-ambient-header .training-logo {
    width: 92px;
  }

  .access-module-name {
    color: #111111;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .055em;
    white-space: nowrap;
  }

  .access-ambient-content {
    position: relative;
    z-index: 5;
    align-self: center;
    justify-self: center;
    width: min(100%, 620px);
    margin-top: 8px;
    text-align: center;
  }

  .access-ambient-content h1 {
    margin: 0;
    color: #171717;
    font-size: clamp(30px, 3vw, 38px);
    font-weight: 600;
    line-height: 1.08;
    letter-spacing: -.035em;
  }

  .access-ambient-content form {
    margin-top: 24px;
  }

  .access-prompt-input {
    min-height: 106px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
  }

  .access-prompt-input input {
    width: min(100%, 520px);
    height: auto;
    min-width: 0;
    padding: 0;
    border: 0;
    border-radius: 0;
    outline: none;
    background: transparent;
    box-shadow: none;
    color: #171717;
    caret-color: #5b8cff;
    font-size: clamp(48px, 5.7vw, 70px);
    font-weight: 300;
    line-height: 1;
    letter-spacing: -.05em;
    text-align: center;
  }

  .access-prompt-input input::placeholder {
    color: #d7d7dc;
  }

  .access-prompt-input input:focus {
    border: 0;
    box-shadow: none;
  }

  .access-ambient-content .training-button.is-wide {
    width: auto;
    min-width: 184px;
    min-height: 52px;
    margin-top: 24px;
    padding: 0 30px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 650;
  }

  .access-form-note {
    max-width: 520px;
    margin: 24px auto 0;
    color: #858580;
    font-size: 11px;
    line-height: 1.6;
  }

  .access-ambient-stage {
    position: absolute;
    z-index: 1;
    inset: 92px 0 90px;
    pointer-events: none;
  }

  .ambient-card,
  .ambient-float {
    position: absolute;
    border: 1px solid rgba(17,17,17,.045);
    background: rgba(250,250,249,.78);
    box-shadow: 0 10px 35px rgba(0,0,0,.018);
    backdrop-filter: blur(5px);
  }

  .ambient-card {
    border-radius: 18px;
  }

  .ambient-dashboard {
    top: 10%;
    left: 50%;
    width: 290px;
    padding: 16px;
    transform: translateX(-50%);
    opacity: .48;
    animation: ambient-dashboard-drift 8s ease-in-out infinite;
  }

  .ambient-dashboard-top,
  .ambient-dashboard-row {
    display: flex;
    align-items: center;
  }

  .ambient-dashboard-top {
    gap: 9px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(17,17,17,.035);
  }

  .ambient-dashboard-row {
    gap: 10px;
    padding-top: 12px;
  }

  .ambient-dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    background: #d9d9d5;
  }

  .ambient-line {
    width: 88px;
    height: 8px;
    border-radius: 999px;
    background: #e4e4e0;
  }

  .ambient-line-long {
    width: 126px;
  }

  .ambient-line-short {
    width: 64px;
  }

  .ambient-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #deded9;
  }

  .ambient-pill {
    width: 54px;
    height: 18px;
    margin-left: auto;
    border-radius: 999px;
    background: #ecece8;
  }

  .ambient-pill-dark {
    background: #ddddda;
  }

  .ambient-float {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 14px;
    color: #6e6e69;
    opacity: .64;
  }

  .ambient-float strong,
  .ambient-float small {
    display: block;
  }

  .ambient-float strong {
    color: #5f5f5a;
    font-size: 10px;
    font-weight: 650;
  }

  .ambient-float small {
    margin-top: 2px;
    color: #969691;
    font-size: 8px;
  }

  .ambient-profile {
    left: 14%;
    top: 39%;
    animation: ambient-drift-one 7.5s ease-in-out infinite;
  }

  .ambient-profile-avatar {
    width: 28px;
    height: 28px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: #e8e8e4;
    color: #8a8a85;
    font-size: 9px;
    font-weight: 700;
  }

  .ambient-message {
    right: 12%;
    top: 35%;
    max-width: 235px;
    animation: ambient-drift-two 9s ease-in-out infinite;
  }

  .ambient-message p {
    margin: 0;
    color: #777772;
    font-size: 9px;
    line-height: 1.35;
  }

  .ambient-message-dot {
    width: 8px;
    height: 8px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: #d8d8d4;
  }

  .ambient-qualified {
    right: 17%;
    bottom: 18%;
    animation: ambient-drift-three 8.5s ease-in-out infinite;
  }

  .ambient-qualified > span {
    width: 28px;
    height: 28px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: #edede9;
    color: #767671;
    font-size: 11px;
    font-weight: 700;
  }

  .ambient-step {
    left: 20%;
    bottom: 20%;
    animation: ambient-drift-four 10s ease-in-out infinite;
  }

  .ambient-step > span {
    color: #a1a19c;
    font-size: 8px;
    font-weight: 700;
  }

  .ambient-step strong {
    font-size: 9px;
  }

  .access-ambient-footer {
    position: relative;
    z-index: 6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 36px;
  }

  .access-legal {
    color: #a0a09b;
    font-size: 9px;
    font-weight: 500;
  }

  .access-centered-socials {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
  }

  .access-centered-socials a {
    color: #000;
    font-size: 1.28rem;
    line-height: 1;
    transition: color 0.2s ease;
  }

  @keyframes ambient-dashboard-drift {
    0%, 100% { transform: translate(-50%, 0); }
    50% { transform: translate(-50%, -7px); }
  }

  @keyframes ambient-drift-one {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(6px, -8px); }
  }

  @keyframes ambient-drift-two {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(-7px, 6px); }
  }

  @keyframes ambient-drift-three {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(5px, -6px); }
  }

  @keyframes ambient-drift-four {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(-5px, -7px); }
  }

  @media (max-width: 1000px) {
    .ambient-profile { left: 6%; }
    .ambient-message { right: 5%; }
    .ambient-qualified { right: 7%; }
    .ambient-step { left: 8%; }
  }

  @media (max-width: 760px) {
    .access-ambient {
      padding: 24px 22px 18px;
    }

    .access-module-name {
      font-size: 10px;
    }

    .access-ambient-stage {
      opacity: .42;
    }

    .ambient-profile,
    .ambient-message,
    .ambient-qualified,
    .ambient-step {
      display: none;
    }

    .ambient-dashboard {
      top: 14%;
      width: 250px;
    }

    .access-ambient-content {
      margin-top: 0;
    }
  }

  @media (max-width: 520px) {
    .access-ambient-header .training-logo {
      width: 84px;
    }

    .access-module-name {
      font-size: 9px;
      letter-spacing: .035em;
    }

    .access-ambient-content h1 {
      font-size: 30px;
    }

    .access-prompt-input {
      min-height: 82px;
    }

    .access-prompt-input input {
      font-size: 44px;
    }

    .ambient-dashboard {
      width: 220px;
      opacity: .36;
    }

    .access-legal {
      max-width: 180px;
      line-height: 1.35;
    }
  }

  /* Full-viewport access screen */
  .access-white-shell {
    width: 100%;
    min-height: 100vh;
    display: grid;
    grid-template-columns: minmax(0, 1.16fr) minmax(420px, .84fr);
    background: #ffffff;
  }

  .access-white-brand {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    padding: clamp(50px, 5.5vw, 84px);
    background: #f7f7f1;
  }

  .access-white-brand-top .training-logo {
    width: 112px;
  }

  .access-white-brand-copy {
    max-width: 720px;
    margin: auto 0;
    padding: 64px 0 88px;
  }

  .access-white-brand-copy h1 {
    max-width: 700px;
    margin: 0;
    color: #111111;
    font-size: clamp(62px, 7vw, 100px);
    font-weight: 900;
    line-height: .9;
    letter-spacing: -.075em;
  }

  .access-white-brand-copy p {
    max-width: 590px;
    margin: 32px 0 0;
    color: #4f4f4b;
    font-size: clamp(17px, 1.45vw, 21px);
    line-height: 1.5;
  }

  .access-white-copyright {
    margin-top: auto;
    color: #8d8d88;
    font-size: 11px;
    font-weight: 500;
  }

  .access-white-login {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: clamp(52px, 6vw, 92px);
    background: #ffffff;
    box-shadow: -18px 0 45px rgba(0, 0, 0, .025);
  }

  .access-white-login-inner {
    width: min(100%, 430px);
  }

  .access-white-login-inner > .training-logo {
    width: 108px;
    margin-bottom: 96px;
  }

  .access-white-login-copy h2 {
    margin: 0;
    color: #111111;
    font-size: 42px;
    font-weight: 900;
    line-height: 1;
    letter-spacing: -.055em;
  }

  .access-white-login-copy p {
    max-width: 370px;
    margin: 13px 0 0;
    color: #707070;
    font-size: 14px;
    line-height: 1.5;
  }

  .access-white-login-inner form {
    margin-top: 42px;
  }

  .access-white-login-inner label {
    display: block;
    margin-bottom: 9px;
    color: #303030;
    font-size: 12px;
    font-weight: 800;
  }

  .access-white-login-inner input {
    width: 100%;
    height: 58px;
    padding: 0 2px;
    border: 0;
    border-bottom: 1.5px solid #bebeba;
    border-radius: 0;
    background: transparent;
    color: #111111;
    font-size: 16px;
    outline: none;
  }

  .access-white-login-inner input::placeholder {
    color: #aaaaa5;
  }

  .access-white-login-inner input:focus {
    border-bottom-color: #111111;
    box-shadow: none;
  }

  .access-white-login-inner .training-button.is-wide {
    min-height: 54px;
    margin-top: 34px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 800;
  }

  .access-white-login-inner > small {
    display: block;
    margin-top: 24px;
    color: #9b9b96;
    font-size: 10px;
    line-height: 1.45;
    text-align: center;
  }

  .eyebrow, .section-eyebrow {
    color: var(--training-muted);
    font-size: 11px;
    font-weight: 850;
    letter-spacing: .1em;
    text-transform: uppercase;
  }

  .training-button {
    min-height: 44px;
    padding: 0 20px;
    border: 1px solid var(--training-ink);
    border-radius: 8px;
    background: var(--training-ink);
    color: white;
    font-size: 13px;
    font-weight: 760;
    cursor: pointer;
  }

  .training-button.is-secondary {
    border-color: var(--training-line);
    background: white;
    color: var(--training-ink);
  }

  .training-button.is-wide { width: 100%; margin-top: 18px; }
  .training-button:disabled, .audio-button:disabled { opacity: .4; cursor: not-allowed; }

  .training-error {
    margin-top: 14px;
    padding: 12px 14px;
    border: 1px solid #e5b4b1;
    border-radius: 10px;
    background: #fff7f6;
    color: var(--training-error);
    font-size: 13px;
  }

  .access-ui {
    width: min(1180px, calc(100% - 48px));
    min-height: 100vh;
    margin: 0 auto;
    display: grid;
    grid-template-columns: minmax(0, 1.12fr) minmax(360px, .88fr);
    gap: clamp(34px, 5vw, 72px);
    align-items: center;
    padding: 34px 0;
    background: #fff;
  }

  .access-ui-main {
    min-width: 0;
  }

  .access-ui-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 54px;
  }

  .access-ui-header .training-logo {
    width: 94px;
  }

  .access-ui-header > span {
    color: #777;
    font-size: 11px;
    font-weight: 700;
  }

  .access-ui-copy {
    max-width: 690px;
  }

  .access-ui-copy h1 {
    max-width: 650px;
    margin: 0;
    color: var(--training-ink);
    font-size: clamp(50px, 6vw, 78px);
    font-weight: 880;
    letter-spacing: -.065em;
    line-height: .94;
  }

  .access-ui-copy p {
    max-width: 590px;
    margin: 22px 0 0;
    color: #555;
    font-size: 17px;
    line-height: 1.5;
  }

  .access-photo-grid {
    display: grid;
    grid-template-columns: 1.55fr .78fr;
    grid-template-rows: 155px 155px;
    gap: 10px;
    margin-top: 36px;
    min-height: 320px;
  }

  .access-photo-grid img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    border-radius: 14px;
    background: #eee;
  }

  .access-photo-main {
    grid-row: 1 / 3;
  }

  .access-login-panel {
    width: 100%;
    max-width: 410px;
    justify-self: end;
    padding: 30px;
    background: #f4f4f2;
    border-radius: 20px;
  }

  .access-login-copy h2 {
    margin: 0;
    color: var(--training-ink);
    font-size: 28px;
    font-weight: 850;
    letter-spacing: -.035em;
    line-height: 1.05;
  }

  .access-login-copy p {
    margin: 10px 0 0;
    color: #6b6b67;
    font-size: 13px;
    line-height: 1.45;
  }

  .access-login-panel form {
    margin-top: 28px;
  }

  .access-login-panel label {
    display: block;
    margin-bottom: 8px;
    color: #333;
    font-size: 12px;
    font-weight: 800;
  }

  .access-login-panel input {
    width: 100%;
    height: 56px;
    padding: 0 15px;
    border: 1px solid #d6d6d2;
    border-radius: 10px;
    background: #fff;
    outline: none;
    font-size: 15px;
  }

  .access-login-panel input:focus {
    border-color: var(--training-ink);
    box-shadow: 0 0 0 3px #0000000d;
  }

  .access-login-panel .training-button.is-wide {
    min-height: 52px;
    margin-top: 12px;
    font-size: 14px;
  }

  .access-login-panel > small {
    display: block;
    margin-top: 18px;
    color: #8b8b86;
    font-size: 10px;
    line-height: 1.4;
  }

  /* Open training overview screen */
  .intro-open {
    position: relative;
    min-height: 100vh;
    overflow: hidden;
    display: grid;
    grid-template-rows: auto 1fr auto;
    padding: 30px 44px 24px;
    background: #ffffff;
  }

  .intro-open-header {
    position: relative;
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 56px;
  }

  .intro-open-header .training-logo {
    width: 92px;
  }

  .intro-open-module-name {
    color: #111111;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .055em;
    white-space: nowrap;
  }

  .intro-background-details {
    position: absolute;
    z-index: 1;
    inset: 86px 0 54px;
    pointer-events: none;
  }

  .intro-bg-card {
    position: absolute;
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 10px 12px;
    border: 1px solid rgba(17, 17, 17, .035);
    border-radius: 13px;
    background: rgba(248, 248, 246, .78);
    box-shadow: 0 10px 28px rgba(0, 0, 0, .012);
    opacity: .32;
    backdrop-filter: blur(4px);
  }

  .intro-bg-card-one {
    top: 11%;
    left: 46%;
    width: 215px;
    animation: intro-bg-float-one 10s ease-in-out infinite;
  }

  .intro-bg-card-two {
    left: 5%;
    bottom: 16%;
    width: 150px;
    opacity: .24;
    animation: intro-bg-float-two 12s ease-in-out infinite;
  }

  .intro-bg-card-three {
    right: 4%;
    bottom: 11%;
    width: 180px;
    opacity: .26;
    animation: intro-bg-float-three 11s ease-in-out infinite;
  }

  .intro-bg-avatar {
    width: 25px;
    height: 25px;
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: #deded9;
    color: #8b8b85;
    font-size: 8px;
    font-weight: 700;
  }

  .intro-bg-dot {
    width: 8px;
    height: 8px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: #d6d6d1;
  }

  .intro-bg-check {
    width: 24px;
    height: 24px;
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: #e1e1dd;
    color: #8d8d87;
    font-size: 9px;
    font-weight: 700;
  }

  .intro-bg-card > div {
    display: grid;
    gap: 3px;
    min-width: 0;
  }

  .intro-bg-card strong,
  .intro-bg-card small {
    display: block;
  }

  .intro-bg-card strong {
    color: #686863;
    font-size: 9px;
    font-weight: 650;
    line-height: 1.2;
  }

  .intro-bg-card small {
    color: #969691;
    font-size: 7px;
    line-height: 1.25;
  }

  .intro-bg-outreach {
    width: 205px;
  }

  .intro-bg-outreach p {
    margin: 0;
    color: #858580;
    font-size: 8px;
    line-height: 1.35;
  }

  .intro-bg-line {
    display: block;
    height: 6px;
    border-radius: 999px;
    background: #e5e5e1;
  }

  .intro-bg-line-long {
    width: 92px;
  }

  .intro-bg-line-medium {
    width: 76px;
  }

  .intro-bg-line-short {
    width: 46px;
  }

  .intro-bg-pill {
    min-width: 38px;
    height: 16px;
    display: grid;
    place-items: center;
    margin-left: auto;
    padding: 0 7px;
    border-radius: 999px;
    background: #ebebe7;
    color: #989892;
    font-size: 6px;
    font-weight: 750;
    letter-spacing: .06em;
  }

  @keyframes intro-bg-float-one {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(5px, -6px); }
  }

  @keyframes intro-bg-float-two {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(-4px, 5px); }
  }

  @keyframes intro-bg-float-three {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(4px, -5px); }
  }

  .intro-open-main {
    position: relative;
    z-index: 3;
    align-self: center;
    width: min(100%, 1190px);
    margin: 0 auto;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(300px, .55fr);
    gap: clamp(74px, 10vw, 150px);
    align-items: center;
    padding: 56px 0 64px;
  }

  .intro-open-copy {
    max-width: 720px;
  }

  .intro-open-copy h1 {
    max-width: 620px;
    margin: 0;
    color: #171717;
    font-size: clamp(30px, 3vw, 38px);
    font-weight: 600;
    line-height: 1.08;
    letter-spacing: -.035em;
  }

  .intro-open-copy > p {
    max-width: 600px;
    margin: 26px 0 0;
    color: #686864;
    font-size: 16px;
    line-height: 1.65;
  }

  .intro-open-stats {
    display: flex;
    gap: 48px;
    margin-top: 42px;
  }

  .intro-open-stats strong,
  .intro-open-stats span {
    display: block;
  }

  .intro-open-stats strong {
    color: #171717;
    font-size: 21px;
    font-weight: 650;
    letter-spacing: -.025em;
  }

  .intro-open-stats span {
    margin-top: 4px;
    color: #989893;
    font-size: 10px;
    font-weight: 550;
  }

  .intro-account-stat {
    min-width: 0;
    max-width: 190px;
  }

  .intro-account-stat strong {
    overflow: hidden;
    max-width: 190px;
    font-size: 16px;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .intro-open-actions {
    display: flex;
    align-items: center;
    gap: 22px;
    margin-top: 38px;
  }

  .intro-open-actions .training-button {
    min-width: 174px;
    min-height: 50px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 650;
  }

  .intro-status-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
  }

  .intro-status-button:disabled {
    opacity: 1;
    cursor: default;
  }

  .intro-review-content {
    padding: 0;
    border: 0;
    background: transparent;
    color: #777772;
    font-size: 11px;
    font-weight: 550;
    cursor: pointer;
  }

  .intro-review-content:hover {
    color: #111111;
  }

  .intro-change-email {
    padding: 0;
    border: 0;
    background: transparent;
    color: #8c8c87;
    font-size: 11px;
    font-weight: 550;
    cursor: pointer;
  }

  .intro-change-email:hover {
    color: #111111;
  }

  .intro-stepper {
    width: 100%;
    max-width: 500px;
    height: 390px;
    justify-self: end;
    overflow: hidden;
    -webkit-mask-image: linear-gradient(
      to bottom,
      #000 0%,
      #000 68%,
      rgba(0,0,0,.78) 79%,
      rgba(0,0,0,.35) 90%,
      transparent 100%
    );
    mask-image: linear-gradient(
      to bottom,
      #000 0%,
      #000 68%,
      rgba(0,0,0,.78) 79%,
      rgba(0,0,0,.35) 90%,
      transparent 100%
    );
  }

  .intro-stepper.is-ending {
    -webkit-mask-image: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(0,0,0,.28) 9%,
      rgba(0,0,0,.72) 20%,
      #000 32%,
      #000 100%
    );
    mask-image: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(0,0,0,.28) 9%,
      rgba(0,0,0,.72) 20%,
      #000 32%,
      #000 100%
    );
  }

  .intro-stepper-list {
    position: relative;
    padding: 10px 0 54px 150px;
    transition: transform .35s ease;
  }

  .intro-stepper-list::before {
    content: "";
    position: absolute;
    left: 158px;
    top: 18px;
    bottom: 0;
    width: 1px;
    background: #d7d7d3;
  }

.intro-stepper-item {
  position: relative;
  display: grid;
  grid-template-columns: 18px 1fr;
  gap: 14px;
  align-items: start;
  min-height: 70px;
  padding-bottom: 22px;
  opacity: 1;
}

.intro-stepper-item:not(.is-complete):not(.is-current) .intro-stepper-copy {
  opacity: .5;
}

.intro-stepper-item.is-complete .intro-stepper-copy {
  opacity: .72;
}

.intro-stepper-node {
  position: relative;
  z-index: 2;
  width: 16px;
  height: 16px;
  display: grid;
  place-items: center;
  margin-top: 1px;
  border: 1.5px solid #bdbdb8;
  border-radius: 50%;
  background: #ffffff;
  color: #ffffff;
  font-size: 8px;
  font-weight: 700;
}

  .intro-stepper-item.is-complete {
    opacity: .72;
  }

  .intro-stepper-item.is-current {
    opacity: 1;
  }

  .intro-you-are-here {
    position: absolute;
    left: -130px;
    top: -7px;
    width: max-content;
    min-width: 112px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 14px;
    border: 0;
    border-radius: 9px;
    background: #111111;
    color: #ffffff;
    font-family: "Ugly Handwriting", cursive;
    font-size: 14px;
    font-weight: 400;
    line-height: 1;
    letter-spacing: 0;
    white-space: nowrap;
    text-align: center;
    transform: rotate(-2deg);
  }

  .intro-you-are-here::before {
    content: "";
    position: absolute;
    right: -7px;
    top: 50%;
    width: 14px;
    height: 14px;
    background: #111111;
    transform: translateY(-50%) rotate(45deg);
  }

  .intro-stepper-node {
    position: relative;
    z-index: 2;
    width: 16px;
    height: 16px;
    display: grid;
    place-items: center;
    margin-top: 1px;
    border: 1.5px solid #bdbdb8;
    border-radius: 50%;
    background: #ffffff;
    color: #ffffff;
    font-size: 8px;
    font-weight: 700;
  }

  .intro-stepper-item:not(.is-complete):not(.is-current) .intro-stepper-node {
    background: #ffffff;
    border-color: #bdbdb8;
  }

  .intro-stepper-item.is-complete .intro-stepper-node {
    border-color: #111111;
    background: #111111;
  }

  .intro-stepper-item.is-current .intro-stepper-node {
    border-color: #111111;
    background: #111111;
    box-shadow: 0 0 0 5px rgba(17,17,17,.055);
  }

  .intro-stepper-copy strong,
  .intro-stepper-copy small {
    display: block;
  }

  .intro-stepper-copy strong {
    max-width: 290px;
    color: #666661;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
  }

  .intro-stepper-item.is-current .intro-stepper-copy strong {
    color: #171717;
    font-weight: 650;
  }

  .intro-stepper-copy small {
    margin-top: 4px;
    color: #989893;
    font-size: 10px;
  }

  .intro-open-footer {
    position: relative;
    z-index: 4;
    min-height: 36px;
    display: flex;
    align-items: center;
    color: #a0a09b;
    font-size: 9px;
    font-weight: 500;
  }

  @media (max-width: 900px) {
    .intro-open-main {
      width: min(100%, 760px);
      grid-template-columns: 1fr;
      gap: 46px;
      padding: 48px 0 44px;
    }

    .intro-stepper {
      width: min(100%, 470px);
      height: 340px;
      justify-self: start;
    }
  }

  @media (max-width: 600px) {
    .intro-background-details {
      display: none;
    }

    .intro-open {
      padding: 24px 22px 18px;
    }

    .intro-open-header .training-logo {
      width: 84px;
    }

    .intro-open-module-name {
      font-size: 9px;
      letter-spacing: .035em;
    }

    .intro-open-main {
      padding-top: 38px;
    }

    .intro-open-copy > p {
      font-size: 14px;
    }

    .intro-open-stats {
      gap: 20px;
      justify-content: space-between;
    }

    .intro-open-actions {
      align-items: stretch;
      flex-direction: column;
    }

    .intro-review-content {
      min-height: 34px;
    }

    .intro-open-actions .training-button {
      width: 100%;
    }

    .intro-stepper {
      height: 310px;
    }

    .intro-stepper-list {
      padding-left: 126px;
    }

    .intro-stepper-list::before {
      left: 134px;
    }

    .intro-you-are-here {
      left: 2px;
      min-width: 100px;
      height: 30px;
      padding: 0 11px;
      font-size: 12px;
      white-space: nowrap;
    }

    .intro-stepper-item {
      min-height: 62px;
    }
  }

  .intro-ui {
    width: 100%;
    max-width: 900px;
  }

  .intro-ui-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .intro-ui-header .training-logo {
    width: 92px;
  }

  .intro-ui-copy {
    max-width: 760px;
    margin-top: 76px;
  }

  .intro-ui-copy h1 {
    margin: 0;
    color: var(--training-ink);
    font-size: clamp(48px, 6vw, 72px);
    font-weight: 880;
    line-height: .96;
    letter-spacing: -.064em;
  }

  .intro-ui-copy p {
    max-width: 650px;
    margin: 20px 0 0;
    color: #555;
    font-size: 16px;
    line-height: 1.55;
  }

  .intro-module-card {
    margin-top: 42px;
    overflow: hidden;
    background: #f5f5f3;
    border-radius: 18px;
  }

  .intro-module-row {
    display: grid;
    grid-template-columns: 42px 1fr;
    gap: 16px;
    align-items: start;
    padding: 20px 22px;
  }

  .intro-module-row + .intro-module-row {
    border-top: 1px solid #e5e5e1;
  }

  .intro-module-row > span {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #111;
    color: #fff;
    font-size: 9px;
    font-weight: 850;
  }

  .intro-module-row > div {
    display: grid;
    gap: 4px;
  }

  .intro-module-row strong {
    font-size: 15px;
    font-weight: 850;
    letter-spacing: -.02em;
  }

  .intro-module-row small {
    color: #6d6d68;
    font-size: 12px;
    line-height: 1.45;
  }

  .intro-ui-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 28px;
    margin-top: 30px;
  }

  .intro-ui-stats {
    display: flex;
    gap: 24px;
    color: #7b7b76;
    font-size: 11px;
  }

  .intro-ui-stats strong {
    display: block;
    color: var(--training-ink);
    font-size: 16px;
  }

  .intro-ui-bottom .training-button {
    min-width: 170px;
  }

  .access-card, .intro-card, .result-card { width: 100%; max-width: 680px; }
  .access-card { max-width: 480px; }
  .access-card h1, .intro-card h1, .result-card h1 {
    margin: 15px 0 14px;
    font-size: clamp(46px, 7vw, 78px);
    line-height: .97;
    letter-spacing: -.06em;
  }
  .access-card p, .intro-card p, .result-card p {
    color: var(--training-muted);
    font-size: 16px;
    line-height: 1.6;
  }
  .access-card form { margin-top: 30px; }
  .access-card form > label { display: block; margin-bottom: 8px; font-size: 13px; font-weight: 800; }
  .access-card input {
    width: 100%;
    height: 54px;
    padding: 0 15px;
    border: 1px solid var(--training-line);
    border-radius: 12px;
    outline: none;
  }
  .access-card input:focus { border-color: var(--training-ink); box-shadow: 0 0 0 3px #00000012; }
  .intro-card { max-width: 820px; }
  .intro-card > header { display: flex; justify-content: space-between; }
  .text-button, .audio-button { border: 0; background: transparent; color: var(--training-muted); font-size: 13px; font-weight: 750; cursor: pointer; }
  .intro-stats { display: flex; gap: 32px; margin: 32px 0; padding: 20px 0; border-block: 1px solid var(--training-line); color: var(--training-muted); font-size: 13px; }
  .intro-stats strong { display: block; color: var(--training-ink); font-size: 20px; }

  .training-player, .final-quiz, .result-page {
    width: calc(100% - 56px);
    max-width: 1180px;
    min-height: 100vh;
    margin: 0 auto;
    padding: 14px 42px 16px;
    background: var(--training-white);
    border-inline: 0px solid var(--training-paper-edge);
  }

  .training-player {
    display: flex;
    flex-direction: column;
  }
  .training-header {
    height: 72px;
    display: grid;
    grid-template-columns: 1fr minmax(280px, 430px) 1fr;
    align-items: center;
    gap: 24px;
  }

  .training-header .training-logo {
    width: 92px;
    justify-self: start;
  }

  .training-header-title {
    justify-self: end;
    color: #111111;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .055em;
    white-space: nowrap;
  }

  .training-header-progress {
    width: 100%;
    max-width: 430px;
    justify-self: center;
    overflow: visible;
  }

  .training-step-progress {
    position: relative;
    display: grid;
    grid-template-columns: repeat(10, 1fr);
    align-items: center;
    width: 100%;
    height: 22px;
    padding: 0 8px;
    -webkit-mask-image: linear-gradient(
      to right,
      transparent 0,
      rgba(0,0,0,.5) 7%,
      #000 18%,
      #000 82%,
      rgba(0,0,0,.5) 93%,
      transparent 100%
    );
    mask-image: linear-gradient(
      to right,
      transparent 0,
      rgba(0,0,0,.5) 7%,
      #000 18%,
      #000 82%,
      rgba(0,0,0,.5) 93%,
      transparent 100%
    );
  }

  /*
   * FINAL QUIZ PROGRESS
   *
   * The quiz still has 12 real progress nodes.
   * The viewport shows about 8 node positions at once.
   * The 8th node fades into the right edge while questions
   * 9–12 remain clipped outside the viewport.
   *
   * The rail does NOT compress to 8 steps.
   */
  .final-quiz-header-progress {
    position: relative;
    height: 72px;
    align-self: stretch;
    overflow: visible;
  }

  /*
   * This viewport sits on the exact vertical midpoint of the
   * 72px header, matching the Tribe logo and module title.
   */
  .quiz-step-viewport {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 100%;
    height: 22px;
    overflow: hidden;
    transform: translate(-50%, -50%);
  }

  /*
   * Full 12-step rail is 150% of the viewport width.
   * 12 / 1.5 = 8 visible step positions.
   */
  .quiz-step-progress {
    width: 150%;
    grid-template-columns: repeat(12, 1fr);
    padding-inline: 8px;
    -webkit-mask-image: none;
    mask-image: none;
    transition: transform .32s ease;
    will-change: transform;
  }

  /*
   * Beginning: no left fade because nothing is hidden there.
   * Fade the right edge where step 8 exits the viewport.
   */
  .quiz-step-viewport.has-hidden-after:not(.has-hidden-before) {
    -webkit-mask-image: linear-gradient(
      to right,
      #000 0%,
      #000 78%,
      rgba(0,0,0,.9) 84%,
      rgba(0,0,0,.55) 91%,
      rgba(0,0,0,.18) 97%,
      transparent 100%
    );
    mask-image: linear-gradient(
      to right,
      #000 0%,
      #000 78%,
      rgba(0,0,0,.9) 84%,
      rgba(0,0,0,.55) 91%,
      rgba(0,0,0,.18) 97%,
      transparent 100%
    );
  }

  /*
   * Middle: there are hidden questions on both sides.
   */
  .quiz-step-viewport.has-hidden-before.has-hidden-after {
    -webkit-mask-image: linear-gradient(
      to right,
      transparent 0%,
      rgba(0,0,0,.18) 3%,
      rgba(0,0,0,.55) 9%,
      rgba(0,0,0,.9) 16%,
      #000 22%,
      #000 78%,
      rgba(0,0,0,.9) 84%,
      rgba(0,0,0,.55) 91%,
      rgba(0,0,0,.18) 97%,
      transparent 100%
    );
    mask-image: linear-gradient(
      to right,
      transparent 0%,
      rgba(0,0,0,.18) 3%,
      rgba(0,0,0,.55) 9%,
      rgba(0,0,0,.9) 16%,
      #000 22%,
      #000 78%,
      rgba(0,0,0,.9) 84%,
      rgba(0,0,0,.55) 91%,
      rgba(0,0,0,.18) 97%,
      transparent 100%
    );
  }

  /*
   * End: no right fade because question 12 is the end.
   */
  .quiz-step-viewport.has-hidden-before:not(.has-hidden-after) {
    -webkit-mask-image: linear-gradient(
      to right,
      transparent 0%,
      rgba(0,0,0,.18) 3%,
      rgba(0,0,0,.55) 9%,
      rgba(0,0,0,.9) 16%,
      #000 22%,
      #000 100%
    );
    mask-image: linear-gradient(
      to right,
      transparent 0%,
      rgba(0,0,0,.18) 3%,
      rgba(0,0,0,.55) 9%,
      rgba(0,0,0,.9) 16%,
      #000 22%,
      #000 100%
    );
  }

  /*
   * Final Quiz is visually below the bar but absolutely
   * positioned so it does not pull the bar above center.
   */
  .final-quiz-header-progress .training-progress-context {
    position: absolute;
    left: 50%;
    top: calc(50% + 22px);
    width: max-content;
    margin-top: 0;
    transform: translateX(-50%);
  }

  .training-progress-context {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 6px;
    margin-top: 5px;
    text-align: center;
    white-space: nowrap;
  }

  .training-progress-context strong {
    color: #333330;
    font-size: 12px;
    font-weight: 650;
    line-height: 1;
    letter-spacing: -.015em;
  }

  .training-progress-context span {
    color: #333330;
    font-size: 9px;
    font-weight: 550;
    line-height: 1;
  }

  .training-progress-context i {
    color: #c2c2bd;
    font-size: 9px;
    font-style: normal;
  }

  .training-step-line,
  .training-step-active-line {
    position: absolute;
    z-index: 0;
    left: 5%;
    top: 50%;
    height: 3px;
    border-radius: 999px;
    transform: translateY(-50%);
  }

  .training-step-line {
    right: 5%;
    background: #d7d7d3;
  }

  .training-step-active-line {
    overflow: hidden;
    max-width: 90%;
    background: #111111;
    transition: width .28s ease;
  }

  .training-step-active-line::after {
    content: "";
    position: absolute;
    inset: 0;
    width: 30%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255,255,255,.06) 30%,
      rgba(255,255,255,.34) 50%,
      rgba(255,255,255,.06) 70%,
      transparent 100%
    );
    transform: translateX(-150%);
    animation: training-progress-shimmer 1.9s ease-in-out infinite;
  }

  .training-step-node {
    position: relative;
    z-index: 1;
    width: 14px;
    height: 14px;
    display: grid;
    place-items: center;
    justify-self: center;
    border: 1.5px solid #bdbdb8;
    border-radius: 50%;
    background: #ffffff;
    color: #ffffff;
    font-size: 7px;
    font-weight: 700;
  }

  .training-step-node.is-complete {
    border-color: #111111;
    background: #111111;
    color: #ffffff;
  }

  .training-step-node.is-complete svg {
    display: block;
    stroke: currentColor;
  }

  .training-step-node.is-current {
    border: 2.5px solid #111111;
    background: #ffffff;
    box-shadow: 0 0 0 4px rgba(17,17,17,.055);
  }


  .training-next-group {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    min-width: 0;
  }

  .section-stage {
    flex: 1 1 auto;
    min-height: min(640px, calc(100vh - 210px));
    display: grid;
    align-items: center;
    animation: section-in .22s ease;
  }

  .training-section {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(400px, 1.05fr);
    gap: 6%;
    align-items: center;
    padding: 42px 4%;
  }
  .section-copy h1 {
    max-width: 620px;
    margin: 13px 0 20px;
    font-size: clamp(34px, 3.8vw, 58px);
    line-height: 1.02;
    letter-spacing: -.05em;
  }
  .section-copy > p { max-width: 540px; margin: 0; color: #373737; font-size: clamp(16px, 1.3vw, 20px); line-height: 1.5; }
  .section-visual { position: relative; min-height: 390px; display: grid; align-items: center; padding: 34px; border-radius: 25px; background: var(--training-soft); overflow: hidden; }

  .micro-question { width: 100%; max-width: 760px; margin: auto; padding: 46px 0; }
  .micro-question-label { color: var(--training-muted); font-size: 11px; font-weight: 900; letter-spacing: .09em; }
  .micro-question h2 {
    margin: 13px 0 28px;
    color: var(--training-ink);
    font-size: clamp(38px, 5vw, 56px);
    font-weight: 850;
    line-height: 1.02;
    letter-spacing: -.055em;
  }
  .micro-options { display: grid; gap: 10px; }
  .micro-option {
    display: grid;
    grid-template-columns: 42px 1fr;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 13px;
    border: 2px solid var(--training-line);
    border-radius: 13px;
    background: white;
    color: var(--training-ink);
    text-align: left;
    cursor: pointer;
  }
  .micro-option > span {
    width: 34px;
    height: 34px;
    display: grid;
    place-items: center;
    border-radius: 9px;
    background: var(--training-soft);
    font-size: 12px;
    font-weight: 900;
  }
  .micro-option.is-correct { border-color: var(--training-success); background: #edf8f3; }
  .micro-option.is-correct > span { background: var(--training-success); color: white; }
  .micro-option.is-wrong { border-color: var(--training-error); background: #fff3f2; }
  .micro-option.is-wrong > span { background: var(--training-error); color: white; }
  .answer-feedback { display: grid; gap: 3px; margin-top: 14px; padding: 14px; border-radius: 11px; font-size: 13px; }
  .answer-feedback.is-correct { background: #edf8f3; color: #116846; }
  .answer-feedback.is-wrong { background: #fff3f2; color: #9a302b; }

  .training-controls {
    min-height: 70px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
  }
  .training-controls > div:first-child { display: flex; align-items: center; gap: 10px; }
  .training-controls small { color: #8a8a85; font-size: 11px; }
  .audio-button { padding: 10px; font-size: 12px; font-weight: 650; }

  .quiz-confirm-backdrop {
    position: fixed;
    z-index: 100;
    inset: 0;
    display: grid;
    place-items: center;
    padding: 24px;
    background: rgba(255,255,255,.72);
    backdrop-filter: blur(6px);
  }

  .quiz-confirm-dialog {
    width: min(100%, 430px);
    padding: 26px;
    border: 1px solid #e2e2de;
    border-radius: 18px;
    background: #ffffff;
    box-shadow: 0 24px 70px rgba(0,0,0,.10);
  }

  .quiz-confirm-dialog > strong {
    display: block;
    color: #171717;
    font-size: 21px;
    font-weight: 650;
    line-height: 1.15;
    letter-spacing: -.025em;
  }

  .quiz-confirm-dialog > p {
    margin: 11px 0 0;
    color: #74746f;
    font-size: 13px;
    line-height: 1.55;
  }

  .quiz-confirm-actions {
    display: flex;
    justify-content: flex-end;
    gap: 9px;
    margin-top: 24px;
  }

  .training-document-footer {
    margin-top: auto;
    min-height: 28px;
    padding-top: 9px;
    color: #a0a09b;
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0;
    text-align: left;
  }

  .final-quiz {
    max-width: 980px;
    display: flex;
    flex-direction: column;
  }
  .final-quiz > section {
    flex: 1 1 auto;
    width: min(100%, 760px);
    margin: 0 auto;
    padding: 60px 0;
  }
  .final-quiz h1 {
    margin: 14px 0 30px;
    color: var(--training-ink);
    font-size: clamp(40px, 5vw, 58px);
    font-weight: 850;
    line-height: 1.02;
    letter-spacing: -.055em;
  }
    .final-quiz h1.is-long {
  font-size: clamp(30px, 3vw, 40px);
  line-height: 1.08;
  letter-spacing: -.04em;
}

.final-quiz h1.is-very-long {
  font-size: clamp(24px, 2.3vw, 28px);
  line-height: 1.15;
  letter-spacing: -.025em;
  margin-bottom: 24px;
}

  .final-options { display: grid; gap: 10px; }
  .final-options > button {
    display: grid;
    grid-template-columns: 42px 1fr;
    align-items: center;
    gap: 12px;
    padding: 13px;
    border: 2px solid var(--training-line);
    border-radius: 13px;
    background: white;
    text-align: left;
    cursor: pointer;
  }
  .final-options > button > span { width: 34px; height: 34px; display: grid; place-items: center; border-radius: 9px; background: var(--training-soft); font-weight: 900; }
  .final-options > button.is-selected { border-color: var(--training-ink); background: var(--training-soft); }
  .final-options > button.is-selected > span { background: var(--training-ink); color: white; }
  .final-quiz nav {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    margin-top: 28px;
  }

  .result-page {
    display: flex;
    flex-direction: column;
  }

  .result-header-center {
    justify-self: center;
    color: #333330;
    font-size: 12px;
    font-weight: 650;
    line-height: 1;
    letter-spacing: -.015em;
    white-space: nowrap;
  }

  .result-stage {
    flex: 1 1 auto;
    display: grid;
    place-items: center;
    padding: 64px 0 72px;
  }

  .quiz-result-card {
    width: min(100%, 680px);
    text-align: center;
  }

  .quiz-result-count {
    display: flex;
    align-items: baseline;
    justify-content: center;
  }

  .quiz-result-count strong {
    font-size: clamp(72px, 9vw, 100px);
    font-weight: 800;
    line-height: .82;
    letter-spacing: -.075em;
  }

  .quiz-result-count span {
    margin-left: 8px;
    color: #777777;
    font-size: clamp(18px, 2.3vw, 27px);
    font-weight: 500;
  }

  .quiz-result-card h1 {
    margin: 24px 0 0;
    color: #171717;
    font-size: clamp(32px, 4vw, 44px);
    font-weight: 800;
    line-height: 1.02;
    letter-spacing: -.048em;
  }

  .quiz-result-detail {
    max-width: 520px;
    margin: 14px auto 0;
    color: #696965;
    font-size: 13px;
    line-height: 1.55;
  }

  .quiz-result-actions {
    display: grid;
    justify-items: center;
    gap: 14px;
    margin-top: 32px;
  }

  .quiz-result-actions > .training-button {
    width: min(100%, 320px);
    min-height: 52px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 760;
  }

  .quiz-result-text-action {
    padding: 4px 0;
    border: 0;
    background: transparent;
    color: #777772;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
  }

  .quiz-result-text-action:hover {
    color: #111111;
  }

  .quiz-result-text-action:disabled {
    opacity: .4;
    cursor: not-allowed;
  }

  .visual-list { display: grid; gap: 10px; }
  .visual-list > div { display: grid; grid-template-columns: 38px 1fr; gap: 12px; align-items: center; padding: 14px; border-radius: 12px; background: white; }
  .visual-list > div > span { width: 30px; height: 30px; display: grid; place-items: center; border-radius: 50%; background: var(--training-ink); color: white; font-size: 11px; font-weight: 900; }
  .visual-list strong, .visual-list small { display: block; }
  .visual-list small { margin-top: 4px; color: var(--training-muted); }
  .big-statement { text-align: center; }
  .big-statement strong { display: block; font-size: clamp(58px, 8vw, 110px); line-height: .9; letter-spacing: -.08em; }
  .big-statement span { display: block; margin-top: 18px; color: var(--training-muted); font-weight: 800; }
  .script-message { max-width: 530px; margin: auto; }
  .script-message > label { color: var(--training-muted); font-size: 10px; font-weight: 900; letter-spacing: .08em; }
  .script-message > p { margin: 10px 0 0; padding: 20px; border-radius: 20px 20px 20px 5px; background: var(--training-ink); color: white; font-size: 15px; line-height: 1.55; }
  .script-breakdown { display: grid; gap: 9px; margin-top: 15px; }
  .script-breakdown > div { padding: 12px; border-radius: 10px; background: white; }
  .script-breakdown strong, .script-breakdown span { display: block; }
  .script-breakdown span { margin-top: 3px; color: var(--training-muted); font-size: 12px; }
  .flow-branch { display: grid; gap: 10px; }
  .flow-branch > div { padding: 16px; border-radius: 12px; background: white; }
  .flow-branch strong, .flow-branch span { display: block; }
  .flow-branch span { margin-top: 5px; color: var(--training-muted); font-size: 13px; line-height: 1.4; }

  /* What is Tribe */
  .tribe-gathering-scene { position: relative; width: 100%; height: 340px; }
  .gathering-glow { position: absolute; inset: 18% 20%; border-radius: 50%; background: radial-gradient(circle, #fff 0, #e8e8e8 45%, transparent 72%); animation: breathe 3s ease-in-out infinite; }
  .gathering-card { position: absolute; z-index: 3; left: 50%; top: 50%; width: 205px; padding: 18px; transform: translate(-50%,-50%); border: 1px solid #d5d5d5; border-radius: 17px; background: #fff; box-shadow: 0 18px 40px #00000012; text-align: center; }
  .gathering-card span,.gathering-card strong,.gathering-card small { display: block; }
  .gathering-card span { font-size: 8px; font-weight: 900; letter-spacing: .08em; }
  .gathering-card strong { margin-top: 8px; font-size: 18px; }
  .gathering-card small { margin-top: 6px; color: var(--training-muted); font-size: 11px; }
  .gathering-person { position: absolute; z-index: 4; width: 46px; height: 46px; display: grid; place-items: center; border: 4px solid var(--training-soft); border-radius: 50%; background: #111; color: #fff; font-size: 10px; font-weight: 900; animation: person-arrive .7s cubic-bezier(.2,.9,.2,1) both; }
  .person-1{left:8%;top:17%;animation-delay:.05s}.person-2{right:7%;top:14%;animation-delay:.15s}.person-3{left:4%;top:55%;animation-delay:.25s}.person-4{right:4%;top:56%;animation-delay:.35s}.person-5{left:24%;bottom:3%;animation-delay:.45s}.person-6{right:23%;bottom:2%;animation-delay:.55s}
  .tribe-gathering-scene.is-connected .gathering-person { animation: person-connect .8s cubic-bezier(.2,.9,.2,1) both; }
  .gathering-caption { position: absolute; left: 50%; bottom: -3px; z-index: 5; width: 250px; transform: translateX(-50%); text-align: center; }
  .gathering-caption strong,.gathering-caption span { display:block}.gathering-caption strong{font-size:13px}.gathering-caption span{margin-top:3px;color:var(--training-muted);font-size:10px}

  /* Why Tribe works */
  .system-scene { position: relative; width: 100%; height: 330px; }
  .system-core { position:absolute;z-index:4;left:50%;top:50%;width:145px;height:145px;display:grid;place-content:center;transform:translate(-50%,-50%);border-radius:50%;background:#111;color:#fff;text-align:center;box-shadow:0 0 0 12px #fff,0 0 0 13px #ddd; }
  .system-core strong,.system-core span{display:block}.system-core strong{font-size:12px;letter-spacing:.07em}.system-core span{margin-top:4px;font-size:10px;color:#bbb}
  .system-node{position:absolute;z-index:5;display:flex;align-items:center;gap:8px;padding:9px 12px;border:1px solid #ddd;border-radius:999px;background:#fff;box-shadow:0 10px 25px #0000000c;font-size:11px;font-weight:800;animation:node-pop .6s cubic-bezier(.2,.9,.2,1) both}
  .system-node b{width:27px;height:27px;display:grid;place-items:center;border-radius:50%;background:#111;color:#fff;font-size:10px}.system-host{left:2%;top:9%}.system-activity{right:0;top:9%;animation-delay:.18s}.system-chat{left:50%;bottom:0;transform:translateX(-50%);animation-delay:.36s}
  .system-lines{position:absolute;inset:0;width:100%;height:100%;fill:none;stroke:#bdbdbd;stroke-width:1.5;stroke-dasharray:6 7}.system-lines path{animation:draw-line 1.1s ease both}
  .system-pulse{position:absolute;left:50%;top:50%;width:150px;height:150px;transform:translate(-50%,-50%);border:1px solid #111;border-radius:50%;animation:system-pulse 2.4s ease-out infinite}

  /* Referral journey */
  .journey-scene{position:relative;width:100%;height:320px}.journey-track{position:absolute;left:10%;right:10%;top:49%;height:4px;border-radius:9px;background:#d8d8d8}.journey-track span{display:block;width:69%;height:100%;border-radius:inherit;background:#111;animation:track-grow 1.3s ease both}
  .journey-stage{position:absolute;top:50%;width:105px;transform:translate(-50%,-50%);text-align:center}.journey-stage i{width:40px;height:40px;display:grid;place-items:center;margin:auto;border:3px solid var(--training-soft);border-radius:50%;background:#111;color:#fff;font-style:normal;font-size:11px;font-weight:900}.journey-stage strong,.journey-stage small{display:block}.journey-stage strong{margin-top:9px;font-size:12px}.journey-stage small{margin-top:2px;color:var(--training-muted);font-size:9px}.stage-find{left:12%}.stage-account{left:50%}.stage-attend{left:88%}
  .journey-person{position:absolute;z-index:5;left:7%;top:28%;width:48px;height:48px;display:grid;place-items:center;border:4px solid #fff;border-radius:50%;background:#111;color:#fff;font-size:10px;font-weight:900;box-shadow:0 8px 22px #0002;animation:journey-move 2.2s cubic-bezier(.65,0,.2,1) forwards}
  .journey-reward{position:absolute;right:3%;top:2%;padding:12px 15px;border-radius:12px;background:#111;color:#fff;opacity:0;transform:scale(.8) rotate(5deg);text-align:center}.journey-reward span,.journey-reward strong{display:block}.journey-reward span{font-size:8px;letter-spacing:.08em}.journey-reward strong{margin-top:3px;font-size:20px}.journey-scene.is-qualified .journey-reward{animation:reward-pop .5s 1.7s cubic-bezier(.2,1.3,.2,1) forwards}

  /* Profile selection */
  .profile-stack-scene{position:relative;width:100%;height:330px}.profile-card{position:absolute;left:50%;top:50%;width:245px;padding:20px;border:1px solid #d7d7d7;border-radius:19px;background:#fff;box-shadow:0 18px 45px #00000012;transition:.6s cubic-bezier(.2,.9,.2,1)}.profile-card strong,.profile-card small{display:block}.profile-card small{margin-top:5px;color:var(--training-muted);font-size:11px;line-height:1.35}.profile-photo{width:58px;height:58px;display:grid;place-items:center;margin-bottom:15px;border-radius:17px;background:linear-gradient(145deg,#1c1c1c,#555);color:#fff;font-size:12px;font-weight:900}
  .profile-back{transform:translate(-75%,-57%) rotate(-10deg);opacity:.35}.profile-middle{transform:translate(-25%,-50%) rotate(8deg);opacity:.55}.profile-front{z-index:3;transform:translate(-50%,-50%)}.profile-tags{display:flex;flex-wrap:wrap;gap:5px;margin-top:13px}.profile-tags span{padding:5px 7px;border-radius:999px;background:#f0f0f0;font-size:8px;font-weight:800}.profile-verdict{display:flex;justify-content:space-between;align-items:center;margin-top:15px;padding-top:12px;border-top:1px solid #e6e6e6;font-size:9px;font-weight:900;letter-spacing:.06em}.profile-verdict b{width:24px;height:24px;display:grid;place-items:center;border-radius:50%;background:#111;color:#fff}
  .profile-focus-ring{position:absolute;z-index:2;left:50%;top:50%;width:280px;height:320px;transform:translate(-50%,-50%);border:2px solid #111;border-radius:30px;opacity:0}.profile-stack-scene.is-decided .profile-back{transform:translate(-115%,-42%) rotate(-18deg);opacity:.12}.profile-stack-scene.is-decided .profile-middle{transform:translate(15%,-42%) rotate(14deg);opacity:.18}.profile-stack-scene.is-decided .profile-front{transform:translate(-50%,-50%) scale(1.05)}.profile-stack-scene.is-decided .profile-focus-ring{animation:focus-ring .8s .2s ease forwards}

  /* Channel orbit */
  .channel-orbit-scene,.channel-collapse{position:relative;width:100%;height:330px}.orbit-ring{position:absolute;left:50%;top:50%;border:1px dashed #c5c5c5;border-radius:50%;transform:translate(-50%,-50%)}.orbit-one{width:280px;height:280px}.orbit-two{width:205px;height:205px}.orbit-center{position:absolute;z-index:3;left:50%;top:50%;width:112px;height:112px;display:grid;place-content:center;transform:translate(-50%,-50%);border-radius:50%;background:#111;color:#fff;text-align:center;box-shadow:0 0 0 12px #fff}.orbit-center span{font-size:22px;font-weight:950}.orbit-center strong{margin-top:4px;font-size:9px}.orbit-logo{position:absolute;z-index:5;width:78px;height:78px;display:grid;place-content:center;justify-items:center;border:4px solid var(--training-soft);border-radius:22px;background:#fff;box-shadow:0 12px 30px #00000015;animation:orbit-float 3.8s ease-in-out infinite}.orbit-logo svg{font-size:27px}.orbit-logo span{margin-top:5px;font-size:8px;font-weight:850}.orbit-instagram{left:5%;top:14%;color:#c13584}.orbit-bumble{right:4%;top:17%;color:#111;background:#ffc629;animation-delay:-1.2s}.orbit-tiktok{left:50%;bottom:0;transform:translateX(-50%);color:#fff;background:#111;animation-delay:-2.4s}
  .channel-collapse{display:grid;place-items:center}.collapsed-logo{position:absolute;width:92px;height:92px;display:grid;place-items:center;border:6px solid var(--training-soft);border-radius:26px;box-shadow:0 16px 35px #0002;animation:collapse-in .8s cubic-bezier(.2,1.2,.2,1) both}.collapsed-logo svg{font-size:34px}.collapsed-logo.instagram{left:25%;top:25%;z-index:2;background:#fff;color:#c13584;transform:rotate(-10deg)}.collapsed-logo.bumble{left:42%;top:18%;z-index:3;background:#ffc629;color:#111;transform:rotate(7deg);animation-delay:.12s}.collapsed-logo.tiktok{left:55%;top:32%;z-index:4;background:#111;color:#fff;transform:rotate(12deg);animation-delay:.24s}.channel-prospect{position:absolute;left:50%;bottom:29px;display:flex;align-items:center;gap:10px;transform:translateX(-50%);white-space:nowrap}.channel-prospect>span{width:38px;height:38px;display:grid;place-items:center;border-radius:50%;background:#111;color:#fff;font-size:9px;font-weight:900}.channel-prospect strong,.channel-prospect small{display:block}.channel-prospect small{margin-top:2px;color:var(--training-muted);font-size:9px}.channel-action-line{position:absolute;left:50%;top:61%;display:flex;align-items:center;gap:8px;transform:translateX(-50%);font-size:8px;font-weight:900;letter-spacing:.08em}.channel-action-line i{width:45px;height:1px;background:#bbb}

  /* Practice games */
  .practice-section{grid-template-columns:minmax(0,1.05fr) minmax(360px,.95fr);gap:4%;padding-inline:1%}.practice-instructions h1{font-size:clamp(30px,3.2vw,48px)}.practice-instructions>p{font-size:15px}.game-choices{display:grid;gap:8px;margin-top:22px}.game-choices button{display:grid;grid-template-columns:30px 1fr;gap:9px;align-items:start;padding:10px;border:1px solid #d8d8d8;border-radius:11px;background:#fff;text-align:left;cursor:pointer}.game-choices button:hover:not(:disabled){border-color:#111;transform:translateY(-1px)}.game-choices button>span{width:25px;height:25px;display:grid;place-items:center;border-radius:7px;background:#f0f0f0;font-size:9px;font-weight:900}.game-choices button strong{font-size:11px;line-height:1.4}.game-feedback{margin-top:10px;padding:10px;border-radius:9px;font-size:11px;font-weight:800}.game-feedback.is-correct{background:#e9f7f0;color:#126b48}.game-feedback.is-wrong{background:#fff1ef;color:#a73630}.game-complete{display:flex;justify-content:space-between;margin-top:20px;padding:15px;border-radius:11px;background:#111;color:#fff;font-size:10px;font-weight:900;letter-spacing:.06em}
  .phone-visual{background:linear-gradient(145deg,#ededed,#fafafa)}.practice-phone{position:relative;width:285px;height:480px;margin:auto;overflow:hidden;border:8px solid #151515;border-radius:35px;background:#fff;box-shadow:0 25px 55px #0003}.practice-phone:before{content:"";position:absolute;z-index:9;left:50%;top:7px;width:82px;height:20px;transform:translateX(-50%);border-radius:99px;background:#151515}.practice-phone>header{height:70px;display:grid;grid-template-columns:25px 1fr 25px;align-items:end;padding:0 13px 9px;border-bottom:1px solid #ececec}.practice-phone>header>span,.practice-phone>header>i{font-size:15px;font-style:normal}.practice-phone>header>div{display:grid;grid-template-columns:32px 1fr;grid-template-rows:auto auto;column-gap:7px;align-items:center}.practice-phone>header b{grid-row:span 2;width:32px;height:32px;display:grid;place-items:center;border-radius:50%;background:#111;color:#fff;font-size:8px}.practice-phone>header strong{font-size:11px}.practice-phone>header small{color:var(--training-muted);font-size:8px}.phone-profile{display:flex;align-items:center;gap:9px;padding:10px 13px;background:#fafafa;border-bottom:1px solid #eee}.phone-profile>span{width:36px;height:36px;display:grid;place-items:center;border-radius:11px;background:#333;color:#fff;font-size:8px;font-weight:900}.phone-profile strong,.phone-profile small{display:block}.phone-profile strong{font-size:10px}.phone-profile small{margin-top:2px;color:var(--training-muted);font-size:7px}.phone-messages{height:330px;display:flex;flex-direction:column;justify-content:flex-end;gap:6px;overflow:hidden;padding:11px}.simulation-messages{height:361px}.phone-empty{margin:auto;color:#aaa;font-size:9px}.phone-bubble{max-width:84%;padding:8px 10px;border-radius:13px;font-size:8.5px;line-height:1.35;animation:bubble-in .3s ease both}.phone-bubble.you{align-self:flex-end;border-bottom-right-radius:4px;background:#111;color:#fff}.phone-bubble.them{align-self:flex-start;border-bottom-left-radius:4px;background:#ededed;color:#111}.typing-dots{align-self:flex-start;display:flex;gap:3px;padding:8px 10px;border-radius:12px;background:#eee}.typing-dots i{width:4px;height:4px;border-radius:50%;background:#777;animation:typing .8s ease infinite}.typing-dots i:nth-child(2){animation-delay:.15s}.typing-dots i:nth-child(3){animation-delay:.3s}.practice-phone>footer{position:absolute;left:0;right:0;bottom:0;height:39px;display:flex;justify-content:space-between;align-items:center;margin:6px 9px;padding:0 9px;border:1px solid #ddd;border-radius:99px;color:#aaa;font-size:8px}.practice-phone>footer b{width:23px;height:23px;display:grid;place-items:center;border-radius:50%;background:#111;color:#fff}.simulation-status{position:absolute;z-index:8;top:19px;left:50%;display:flex;align-items:center;gap:7px;width:max-content;max-width:85%;padding:7px 10px;transform:translateX(-50%);border:1px solid #ddd;border-radius:99px;background:#fff;box-shadow:0 7px 20px #0001;font-size:8px;font-weight:800}.simulation-status i{width:7px;height:7px;border-radius:50%;background:#f0a51c}.simulation-status i.complete{background:var(--training-success)}.qualified-burst{position:absolute;z-index:10;right:18px;top:40%;width:90px;height:90px;display:grid;place-content:center;border:6px solid var(--training-soft);border-radius:50%;background:#111;color:#fff;text-align:center;animation:reward-pop .6s cubic-bezier(.2,1.3,.2,1) both}.qualified-burst span,.qualified-burst strong{display:block}.qualified-burst strong{margin-top:2px;font-size:19px}.simulation-score{display:grid;grid-template-columns:1fr 1fr 1fr;gap:7px;margin-top:20px}.simulation-score>div{padding:11px;border:1px solid #ddd;border-radius:10px}.simulation-score span,.simulation-score strong{display:block}.simulation-score span{font-size:7px;font-weight:900;letter-spacing:.07em}.simulation-score strong{margin-top:5px;font-size:11px}.practice-preview{position:relative;width:100%;height:300px}.preview-profile,.preview-message{position:absolute;top:50%;width:165px;padding:17px;border:1px solid #ddd;border-radius:16px;background:#fff;box-shadow:0 15px 35px #0001}.preview-profile{left:3%;display:flex;gap:9px;align-items:center;transform:translateY(-50%)}.preview-profile>span{width:40px;height:40px;display:grid;place-items:center;border-radius:12px;background:#111;color:#fff;font-size:9px;font-weight:900}.preview-profile strong,.preview-profile small,.preview-message span,.preview-message strong,.preview-message small{display:block}.preview-profile small,.preview-message small{margin-top:3px;color:var(--training-muted);font-size:8px}.preview-message{right:3%;transform:translateY(-50%)}.preview-message span{font-size:7px;font-weight:900;letter-spacing:.07em}.preview-message strong{margin-top:5px;font-size:13px}.preview-arrow{position:absolute;left:50%;top:50%;font-size:32px;transform:translate(-50%,-50%);animation:arrow-nudge 1.4s ease-in-out infinite}.preview-pulse{position:absolute;right:30%;top:50%;width:180px;height:180px;transform:translate(50%,-50%);border:1px solid #bbb;border-radius:50%;animation:system-pulse 2s ease-out infinite}.simulation-map{display:flex;align-items:center;justify-content:center;gap:7px}.simulation-map>div{width:62px;height:62px;display:grid;place-content:center;border-radius:50%;background:#fff;text-align:center;box-shadow:0 8px 20px #0001}.simulation-map span,.simulation-map strong{display:block}.simulation-map span{font-size:8px;color:var(--training-muted)}.simulation-map strong{margin-top:2px;font-size:9px}.simulation-map>i{font-style:normal;color:#999}

  /* Dashboard, goals, pay, rules */
  .dashboard-scene{width:100%;max-width:440px;margin:auto;padding:20px;border:1px solid #d6d6d6;border-radius:18px;background:#fff;box-shadow:0 18px 45px #0001}.dashboard-scene>header{display:flex;justify-content:space-between;padding-bottom:15px;border-bottom:1px solid #eee}.dashboard-scene>header strong,.dashboard-scene>header small{display:block}.dashboard-scene>header small{margin-top:3px;color:var(--training-muted);font-size:9px}.dashboard-scene>header>span{font-size:9px;font-weight:800}.dashboard-person{display:grid;grid-template-columns:38px 1fr auto;gap:9px;align-items:center;padding:16px 0}.dashboard-person>b{width:38px;height:38px;display:grid;place-items:center;border-radius:12px;background:#111;color:#fff;font-size:8px}.dashboard-person strong,.dashboard-person small{display:block}.dashboard-person strong{font-size:11px}.dashboard-person small{margin-top:2px;color:var(--training-muted);font-size:8px}.dashboard-person>i{font-style:normal}.dashboard-timeline{display:grid;grid-template-columns:1fr 25px 1fr 25px 1fr;align-items:center;padding:15px;border-radius:13px;background:#f5f5f5}.dashboard-timeline>div{text-align:center;color:#aaa}.dashboard-timeline>div>span{width:29px;height:29px;display:grid;place-items:center;margin:auto;border:1px solid #ccc;border-radius:50%;font-size:9px;font-weight:900}.dashboard-timeline strong,.dashboard-timeline small{display:block}.dashboard-timeline strong{margin-top:6px;font-size:9px}.dashboard-timeline small{margin-top:2px;font-size:7px}.dashboard-timeline>i{height:1px;background:#ccc}.dashboard-timeline>div.done{color:#111}.dashboard-timeline>div.done>span{border-color:#111;background:#111;color:#fff}.dashboard-timeline>div.reward{animation:reward-pop .5s .6s both}.dashboard-status-change{display:flex;justify-content:space-between;margin-top:12px;padding:11px;border:1px solid #e5e5e5;border-radius:10px}.dashboard-status-change span{font-size:8px;font-weight:900}.dashboard-status-change strong{font-size:10px}.dashboard-scene.is-qualified .dashboard-status-change{background:#111;color:#fff}
  .monthly-rings{position:relative;width:100%;height:330px}.goal-ring{position:absolute;display:grid;place-items:center;border-radius:50%;background:conic-gradient(#111 0 100%,#ddd 0);box-shadow:0 15px 35px #0001;animation:ring-fill 1.5s ease both}.goal-ring:before{content:"";position:absolute;inset:12px;border-radius:50%;background:var(--training-soft)}.goal-ring>div{position:relative;z-index:2;text-align:center}.goal-ring strong,.goal-ring span,.goal-ring small{display:block}.goal-ring strong{font-size:48px;line-height:.9}.goal-ring span{margin-top:7px;font-size:9px;font-weight:850}.goal-ring small{margin-top:3px;color:var(--training-muted);font-size:8px}.referrals-ring{left:5%;top:13%;width:210px;height:210px}.attendance-ring{right:3%;bottom:0;width:150px;height:150px;background:conic-gradient(#111 0 100%,#ddd 0);animation-delay:.35s}.attendance-ring strong{font-size:38px}.month-label{position:absolute;right:6%;top:13%;display:flex;align-items:center;gap:7px;font-size:8px;font-weight:900;letter-spacing:.07em}.month-label i{width:35px;height:1px;background:#111}
  .earnings-counter{text-align:center}.earnings-counter>span{font-size:9px;font-weight:900;letter-spacing:.08em}.earnings-counter>strong{display:block;margin-top:10px;font-size:88px;line-height:.9;letter-spacing:-.08em;animation:money-pop .7s cubic-bezier(.2,1.3,.2,1) both}.earnings-counter>strong i{font-size:28px;font-style:normal;vertical-align:top}.coin-stack{display:flex;justify-content:center;margin-top:25px}.coin-stack b{width:48px;height:48px;display:grid;place-items:center;margin-left:-9px;border:2px solid #111;border-radius:50%;background:#fff;box-shadow:0 8px 18px #0001;animation:coin-drop .5s both}.coin-stack b:nth-child(2){animation-delay:.15s}.coin-stack b:nth-child(3){animation-delay:.3s}.tier-ladder{position:relative;display:grid;gap:8px;padding:10px 0}.tier-step{position:relative;height:46px;padding:8px 12px;overflow:hidden;border-radius:10px;background:#fff}.tier-step span,.tier-step strong{position:relative;z-index:2}.tier-step span{font-size:8px;font-weight:800}.tier-step strong{float:right;font-size:15px}.tier-step i{position:absolute;left:0;bottom:0;height:4px;background:#111;animation:tier-grow .7s ease both}.tier-1 i{animation-delay:.12s}.tier-2 i{animation-delay:.24s}.tier-3 i{animation-delay:.36s}.tier-4 i{animation-delay:.48s}.tier-pointer{position:absolute;right:-5px;top:-13px;font-size:7px;font-weight:900;letter-spacing:.07em}.tier-rule{position:absolute;inset:auto 0 35%;z-index:4;padding:13px;border-radius:10px;background:#111;color:#fff;text-align:center;font-size:8px;font-weight:900;letter-spacing:.07em;animation:reward-pop .5s .6s both}
  .rules-shield-scene{position:relative;width:100%;height:330px}.shield-mark{position:absolute;z-index:3;left:50%;top:48%;width:175px;height:205px;display:grid;place-content:center;transform:translate(-50%,-50%);clip-path:polygon(50% 0,94% 15%,87% 72%,50% 100%,13% 72%,6% 15%);background:#111;color:#fff;text-align:center;filter:drop-shadow(0 18px 24px #0002)}.shield-mark>span{font-size:31px}.shield-mark strong,.shield-mark small{display:block}.shield-mark strong{margin-top:8px;font-size:17px;letter-spacing:.08em}.shield-mark small{margin-top:4px;color:#bbb;font-size:7px;letter-spacing:.06em}.rule-stamp{position:absolute;z-index:4;display:flex;align-items:center;gap:7px;padding:9px 11px;border:1px solid #ccc;border-radius:9px;background:#fff;box-shadow:0 9px 22px #0001;opacity:.25}.rule-stamp span{font-size:8px}.rule-stamp strong{font-size:9px;letter-spacing:.05em}.stamp-accurate{left:1%;top:11%;transform:rotate(-7deg)}.stamp-legitimate{right:0;top:19%;transform:rotate(8deg)}.stamp-respectful{left:4%;bottom:13%;transform:rotate(5deg)}.rule-state-0 .stamp-accurate,.rule-state-1 .stamp-legitimate,.rule-state-2 .stamp-respectful{opacity:1;animation:stamp-in .55s cubic-bezier(.2,1.3,.2,1) both}.shield-caption{position:absolute;right:2%;bottom:8%;text-align:right}.shield-caption strong,.shield-caption span{display:block}.shield-caption strong{font-size:10px}.shield-caption span{margin-top:3px;color:var(--training-muted);font-size:8px}

  @keyframes person-arrive{from{opacity:0;transform:translate(var(--x,0),20px) scale(.5)}to{opacity:1;transform:none}}
  @keyframes person-connect{0%{opacity:0;transform:scale(.6)}70%{transform:scale(1.12)}100%{opacity:1;transform:scale(1)}}
  @keyframes breathe{50%{transform:scale(1.12);opacity:.65}}
  @keyframes node-pop{from{opacity:0;transform:translateY(12px) scale(.8)}to{opacity:1}}
  @keyframes draw-line{from{stroke-dashoffset:180}to{stroke-dashoffset:0}}
  @keyframes system-pulse{0%{opacity:.55;transform:translate(-50%,-50%) scale(.7)}100%{opacity:0;transform:translate(-50%,-50%) scale(1.45)}}
  @keyframes track-grow{from{width:0}}
  @keyframes journey-move{0%{left:7%;top:28%}40%{left:45%;top:28%}75%,100%{left:83%;top:28%}}
  @keyframes reward-pop{from{opacity:0;transform:scale(.7) rotate(7deg)}to{opacity:1;transform:scale(1) rotate(0)}}
  @keyframes focus-ring{from{opacity:0;transform:translate(-50%,-50%) scale(.8)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}
  @keyframes orbit-float{50%{transform:translateY(-10px) rotate(5deg)}}
  @keyframes collapse-in{from{opacity:0;transform:translate(80px,-50px) scale(.3) rotate(40deg)}to{opacity:1}}
  @keyframes bubble-in{from{opacity:0;transform:translateY(7px) scale(.96)}to{opacity:1;transform:none}}
  @keyframes typing{50%{transform:translateY(-3px);opacity:.4}}
  @keyframes arrow-nudge{50%{transform:translate(-40%,-50%)}}
  @keyframes ring-fill{from{background:conic-gradient(#111 0 3%,#ddd 3%)}}
  @keyframes money-pop{from{opacity:0;transform:scale(.6)}to{opacity:1;transform:scale(1)}}
  @keyframes coin-drop{from{opacity:0;transform:translateY(-25px)}to{opacity:1;transform:none}}
  @keyframes tier-grow{from{width:0}}
  @keyframes stamp-in{from{opacity:0;transform:scale(1.5) rotate(-15deg)}to{opacity:1}}

  @keyframes training-progress-shimmer {
    0% { transform: translateX(-150%); opacity: 0; }
    18% { opacity: 1; }
    72% { opacity: 1; }
    100% { transform: translateX(360%); opacity: 0; }
  }

  @keyframes section-in {
    from { opacity: 0; transform: translateY(5px); }
    to { opacity: 1; transform: none; }
  }

  @media (max-width: 900px) {
    .access-ui {
      width: min(100% - 34px, 760px);
      grid-template-columns: 1fr;
      gap: 32px;
      padding: 24px 0 44px;
    }

    .access-ui-header {
      margin-bottom: 42px;
    }

    .access-login-panel {
      max-width: none;
      justify-self: stretch;
    }

    .access-photo-grid {
      grid-template-rows: 140px 140px;
      min-height: 290px;
    }

    .intro-ui {
      max-width: 760px;
    }

    .intro-ui-copy {
      margin-top: 60px;
    }

    .training-player, .final-quiz, .result-page {
      width: 100%;
      padding: 14px 17px 18px;
      border-inline: 0;
      box-shadow: none;
    }
    .training-section { grid-template-columns: 1fr; gap: 25px; padding: 34px 0; }
    .section-copy h1 { max-width: 680px; font-size: clamp(36px, 9vw, 52px); }
    .section-copy > p { max-width: 620px; }
    .section-visual { min-height: 320px; padding: 24px; }
    .training-controls { padding-top: 14px; }
    .training-controls small { display: none; }
    .practice-section { grid-template-columns: 1fr; }
    .phone-visual { min-height: 540px; }
  }

  @media (max-width: 520px) {
    .access-ui {
      width: calc(100% - 28px);
      padding-top: 18px;
    }

    .access-ui-header .training-logo {
      width: 82px;
    }

    .access-ui-copy h1 {
      font-size: 50px;
    }

    .access-ui-copy p {
      font-size: 15px;
    }

    .access-photo-grid {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 170px 120px;
      min-height: 300px;
    }

    .access-photo-main {
      grid-column: 1 / 3;
      grid-row: 1;
    }

    .access-login-panel {
      padding: 22px;
      border-radius: 16px;
    }

    .intro-ui-header .training-logo {
      width: 82px;
    }

    .intro-ui-copy {
      margin-top: 48px;
    }

    .intro-ui-copy h1 {
      font-size: 50px;
    }

    .intro-ui-bottom {
      align-items: stretch;
      flex-direction: column;
    }

    .intro-ui-stats {
      justify-content: space-between;
      gap: 10px;
    }

    .intro-ui-bottom .training-button {
      width: 100%;
    }

    .referral-training.is-centered { padding: 22px; }
    .intro-stats { gap: 15px; justify-content: space-between; }
    .training-controls > div .is-secondary { display: none; }
    .section-visual { padding: 18px; border-radius: 18px; }
    .training-header {
      grid-template-columns: auto minmax(120px, 1fr) auto;
      gap: 10px;
    }
    .training-header .training-logo { width: 84px; }
    .training-header-title {
      font-size: 9px;
      letter-spacing: .035em;
    }

    .result-header-center {
      font-size: 10px;
    }

    .result-stage {
      padding: 48px 0 56px;
    }

    .quiz-result-actions > .training-button {
      width: min(100%, 300px);
    }
    .training-header-progress {
      max-width: 230px;
    }

    .final-quiz-header-progress {
      height: 72px;
    }

    .quiz-step-progress {
      padding-inline: 4px;
    }
    .training-step-progress {
      height: 20px;
      padding-inline: 4px;
    }
    .training-step-node {
      width: 12px;
      height: 12px;
      font-size: 6px;
    }

    .training-next-group {
      gap: 8px;
    }
    .training-progress-context {
      gap: 5px;
      margin-top: 4px;
    }
    .training-progress-context strong {
      font-size: 10px;
    }
    .training-progress-context span,
    .training-progress-context i {
      font-size: 8px;
    }
    .practice-phone { width: 260px; height: 455px; }
    .phone-messages { height: 305px; }
    .simulation-messages { height: 336px; }
    .orbit-logo { width: 66px; height: 66px; border-radius: 18px; }
    .orbit-logo svg { font-size: 23px; }
    .simulation-map { gap: 3px; }
    .simulation-map > div { width: 51px; height: 51px; }
  }


  @media (max-width: 900px) {
    .access-white-shell {
      grid-template-columns: 1fr;
    }

    .access-white-brand {
      min-height: 520px;
      padding: 38px 30px;
    }

    .access-white-brand-copy {
      padding: 56px 0;
    }

    .access-white-brand-copy h1 {
      font-size: clamp(56px, 11vw, 82px);
    }

    .access-white-login {
      min-height: auto;
      padding: 66px 30px 78px;
      box-shadow: none;
    }

    .access-white-login-inner > .training-logo {
      margin-bottom: 62px;
    }
  }

  @media (max-width: 520px) {
    .access-white-brand {
      min-height: 460px;
      padding: 28px 22px;
    }

    .access-white-brand-top .training-logo {
      width: 94px;
    }

    .access-white-brand-copy {
      padding: 42px 0;
    }

    .access-white-brand-copy h1 {
      font-size: 55px;
    }

    .access-white-brand-copy p {
      margin-top: 22px;
      font-size: 15px;
    }

    .access-white-login {
      padding: 56px 22px 70px;
    }

    .access-white-login-inner > .training-logo {
      width: 94px;
      margin-bottom: 54px;
    }

    .access-white-login-copy h2 {
      font-size: 38px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after { animation-duration: .001ms !important; transition-duration: .001ms !important; }
  }
`;